<template>
  <div class="sp-panel">
    <div v-if="isEditMode">
      <EditLayout
        ref="editLayout"
        :layout-state="state"
        @change="applyLayoutDraft"
        @move-shelf="moveLayoutShelf"
      />
    </div>
    <template v-if="!isEditMode">
      <LogoUploader
        :images="logos"
      />
      <div class="sp-title-row">
        <div class="sp-title">
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 16 16" 
            fill="none" 
            class="sp-title__icon"
          >
            <path 
              d="M2 4h12M2 8h12M2 12h8" 
              stroke="currentColor" 
              stroke-width="1.5" 
              stroke-linecap="round"
            />
            <circle 
              cx="13" 
              cy="12" 
              r="2" 
              stroke="currentColor" 
              stroke-width="1.3" 
              fill="none"
            />
            <path 
              d="M13 11v2M12 12h2" 
              stroke="currentColor" 
              stroke-width="1" 
              stroke-linecap="round"
            />
          </svg>
          Shelf Planogram
        </div>
        <div class="d-flex align-items-center">
          <button
            title="Auto Fill"
            class="sp-icon-btn sp-auto-fill-trigger mr-1"
            @click.stop="openAutoFillProductBrowser($event)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-wand-sparkles h-3.5 w-3.5"
            >
              <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
              <path d="m14 7 3 3" /><path d="M5 6v4" /><path d="M19 14v4" /><path d="M10 2v2" /><path d="M7 8H3" /><path d="M21 16h-4" /><path d="M11 3H9" />
            </svg>
          </button>
          <button
            :class="{ active: searchActive }"
            title="Search"
            class="sp-icon-btn"
            @click="searchActive = !searchActive"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <circle
                cx="5.8"
                cy="5.8"
                r="4.3"
                stroke="currentColor"
                stroke-width="1.4"
              />
              <path
                d="M9 9l3 3"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <InlineProductBrowser
        v-if="isAutoFillProductPickerOpen"
        ref="autoFillProductBrowser"
        variant="auto-fill"
        :inline-product-picker-style="autoFillProductPickerStyle"
        :selected-instance="selectedInstance"
        :has-existing-shelf-products="hasPlanogramProducts"
        @close="onAutoFillProductBrowserClose"
        @auto-fill="autoFillShelves"
      />

      <transition name="sp-slide">
        <div 
          v-if="searchActive" 
          class="sp-search-wrap"
        >
          <input
            ref="searchInput"
            v-model="searchQuery"
            class="sp-search"
            placeholder="Search products…"
            @keydown.esc="searchActive = false; searchQuery = ''"
          >
        </div>
      </transition>

      <!-- Section Tabs -->
      <div class="sp-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="sp-tab"
          :class="{ active: activeFixture === tab.key }"
          @click="selectTab(tab.key)"
        >
          {{ tab.label }}
          <span class="sp-tab__badge">{{ shelvesForTab(tab.key).reduce((s,sh)=>s+sh.products.length,0) }}/{{ shelvesForTab(tab.key).length }}</span>
        </button>
      </div>

      <!-- Shelves -->
      <div class="sp-shelves">
        <transition-group 
          :key="activeFixture"
          name="sp-list" 
          tag="div" 
          class="shelves-container"
        >
          <div
            v-for="shelf in filteredShelves"
            :key="shelf.id"
            class="sp-shelf"
            :class="{ 'sp-shelf--active': isShelfActive(shelf) }"
            :data-shelf-id="shelf.id"
            @click="selectShelf(shelf)"
          >
            <!-- Shelf header -->
            <div class="sp-shelf__head">
              <div class="sp-shelf__meta">
                <span class="sp-shelf__name">Shelf {{ shelf.number }}</span>
                <span class="sp-shelf__count">{{ totalFacings(shelf) }} facings</span>
              </div>
              <div class="sp-shelf__actions">
                <!-- Align left -->
                <button
                  class="sp-action-btn"
                  title="Align left"
                  @click="alignShelf(shelf, 'left')"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M1 2v8M3 4h6M3 8h4"
                      stroke="currentColor"
                      stroke-width="1.3"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>

                <!-- Align center -->
                <button
                  class="sp-action-btn"
                  title="Align center"
                  @click="alignShelf(shelf, 'center')"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M6 2v8M2 4h8M3 8h6"
                      stroke="currentColor"
                      stroke-width="1.3"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>

                <!-- Align right -->
                <button
                  class="sp-action-btn"
                  title="Align right"
                  @click="alignShelf(shelf, 'right')"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M11 2v8M3 4h6M5 8h4"
                      stroke="currentColor"
                      stroke-width="1.3"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>

                <ShelfSkewPicker
                  :value="Number(shelf.skew || 0)"
                  :product-spacing="shelf.productSpacing"
                  :canvas-spacing="canvasProductSpacing"
                  @change="setShelfSkew(shelf, $event)"
                  @spacing-change="setShelfProductSpacing(shelf, $event)"
                />

                <ShelfDepthPicker
                  :value="shelf.depth"
                  @change="setShelfDepth(shelf, $event)"
                  @apply-all="applyShelfDepthToAll($event)"
                  @reset-all="resetShelfDepthForAll"
                />

                <!-- Remove shelf -->
                <button
                  class="sp-action-btn sp-action-btn--danger"
                  title="Remove shelf"
                  @click.stop="removeShelf(shelf.id)"
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 11 11"
                    fill="none"
                  >
                    <path
                      d="M1.5 1.5l8 8M9.5 1.5l-8 8"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Products -->
            <div class="sp-products">
              <div
                v-for="(product, productIndex) in shelf.products"
                :key="getShelfProductKey(product, productIndex)"
                class="sp-product"
                :class="{ 'sp-product--highlight': searchQuery && productMatches(product) }"
              >
                <div class="sp-product__img">
                  <span 
                    class="d-flex align-items-center justify-content-center zoom-container"
                    @click.stop="openLightboxModal(product)"
                  >
                    <i class="bi bi-zoom-in" />
                  </span>
                  <img
                    v-if="product.thumbnail"
                    :src="product.thumbnail"
                    :alt="product.name"
                    @error="product.image = null"
                  >
                  <div 
                    v-else 
                    class="sp-product__img-placeholder"
                  >
                    <svg 
                      width="18" 
                      height="18" 
                      viewBox="0 0 18 18" 
                      fill="none"
                    >
                      <rect 
                        x="1" 
                        y="1" 
                        width="16" 
                        height="16" 
                        rx="2" 
                        stroke="currentColor" 
                        stroke-width="1.3" 
                        fill="none"
                      />
                      <path 
                        d="M1 12l4-4 3.5 3.5L13 7l4 5" 
                        stroke="currentColor" 
                        stroke-width="1.3" 
                        stroke-linecap="round" 
                        fill="none"
                      />
                    </svg>
                  </div>
                </div>
                <div class="sp-product__info">
                  <p class="sp-product__name">
                    {{ getShelfProductDisplayName(product) }}
                  </p>
                </div>

                <div class="sp-stepper">
                  <button 
                    :disabled="getProductFacingCount(product) <= 1"
                    class="sp-stepper__btn" 
                    @click="decrement(shelf, product)" 
                  >
                    <svg 
                      width="10" 
                      height="10" 
                      viewBox="0 0 10 10" 
                      fill="none"
                    >
                      <path 
                        d="M2 5h6"
                        stroke="currentColor" 
                        stroke-width="1.5" 
                        stroke-linecap="round"
                      />
                    </svg>
                  </button>
                  <span class="sp-stepper__val">{{ getProductFacingCount(product) }}</span>
                  <button 
                    class="sp-stepper__btn" 
                    @click="increment(shelf, product)"
                  >
                    <svg 
                      width="10" 
                      height="10" 
                      viewBox="0 0 10 10" 
                      fill="none"
                    >
                      <path 
                        d="M5 2v6M2 5h6" 
                        stroke="currentColor" 
                        stroke-width="1.5" 
                        stroke-linecap="round"
                      />
                    </svg>
                  </button>
                </div>
                <TiltPicker
                  :value="Number(product.tilt || 0)"
                  @change="setProductTilt(shelf, product, $event)"
                  @apply-all="applyProductTiltToAll($event)"
                />
                <button 
                  class="sp-action-btn sp-action-btn--danger" 
                  title="Remove product" 
                  @click="removeProduct(shelf, product)"
                >
                  <svg 
                    width="11" 
                    height="11" 
                    viewBox="0 0 11 11" 
                    fill="none"
                  >
                    <path 
                      d="M1.5 1.5l8 8M9.5 1.5l-8 8" 
                      stroke="currentColor" 
                      stroke-width="1.5" 
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <button 
              class="sp-add-product" 
              @click.stop="addProduct(shelf, $event)"
            >
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 12 12" 
                fill="none"
              >
                <path 
                  d="M6 1v10M1 6h10" 
                  stroke="currentColor" 
                  stroke-width="1.5" 
                  stroke-linecap="round"
                />
              </svg>
              Add product
            </button>

            <InlineProductBrowser
              v-if="isInlineProductPickerOpen(shelf)"
              ref="inlineProductBrowser"
              :inline-product-picker-style="inlineProductPickerStyle"
              :selected-instance="selectedInstance"
              @close="onInlineProductBrowserClose"
            />
          </div>
        </transition-group>

        <div 
          v-if="filteredShelves.length === 0 && searchQuery" 
          class="sp-empty"
        >
          <svg 
            width="32" 
            height="32" 
            viewBox="0 0 32 32" 
            fill="none" 
            style="opacity:.35"
          >
            <circle 
              cx="14" 
              cy="14" 
              r="9" 
              stroke="currentColor" 
              stroke-width="1.5"
            />
            <path 
              d="M21 21l7 7" 
              stroke="currentColor" 
              stroke-width="1.5" 
              stroke-linecap="round"
            />
          </svg>
          <p>No products matching <strong>"{{ searchQuery }}"</strong></p>
        </div>
      </div>
      <ProductImageDropdown
        ref="productImageDropdownFields"
        v-model="fileNameIds"
        :is-tab-active="false"
        :is-fields-tab="true"
        @input="onChange('productImage', fileNameIds)"
        @uploadInput="(fileIds) => onChange('productImage',{productIds: fileIds, type: 'uploaded_product_image'})"
      />
      <LightboxModal
        ref="boxPreview"
      />
    </template>
  </div>
</template>

<script>
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker';
import ProductImageDropdown from '@frontend/components/modules/layouts-page/EditorControl/components/ProductImageDropdown.vue';
import { isDeleteConfirmed } from '@frontend/lib/utils/is-delete-confirmed';
import { 
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  IMAGE_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID
 } from '@frontend/constants/templates-field-prefixes';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { FIELD_TYPE_BACKGROND_THEME_IMAGE, FIELD_TYPE_PRODUCT_IMAGE } from '@frontend/constants/type-fields-of-template';
import TiltPicker from './shared/TiltPicker.vue';
import ShelfSkewPicker from '@frontend/components/display-builder/ShelfSkewPicker.vue';
import ShelfDepthPicker from '@frontend/components/display-builder/ShelfDepthPicker.vue';
import LogoUploader from '@frontend/components/display-builder/LogoUploader.vue';
import InlineProductBrowser from '@frontend/components/display-builder/InlineProductBrowser.vue';
import { mapGetters } from 'vuex';
import EditLayout from '@frontend/components/display-builder/EditLayout.vue';
import {
  cloneDisplayBuilderValue,
  DISPLAY_BUILDER_CANVAS_WIDTH,
  DEFAULT_PRODUCT_SPACING,
  DEFAULT_SHELF_COUNT,
  defaultFixtureState,
  fitShelvesToFixture,
  fitShelvesVerticallyToFixture,
  getFixtureOrder,
  MAX_SHELF_PRODUCT_SPACING,
  MIN_SHELF_PRODUCT_SPACING,
  normalizeDisplayBuilderState,
  normalizeShelfDepth,
  normalizeShelfProductSpacing,
  transformShelvesWithFixture,
  swapShelfWithNeighbour,
} from '@frontend/components/display-builder/layout-defaults';
import LightboxModal from '@frontend/components/common/LightboxModal.vue';
import { cloneFabricObject } from '@frontend/lib/fabric/effects/helpers/clone-handler';

let _sid = 10;
const DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE = 'display-builder-upload';

const isUsableShelfId = (id) => {
  return id !== undefined
    && id !== null
    && String(id).trim() !== ''
    && String(id) !== '0';
};

const seedShelfIdCounterFromShelves = (shelves = []) => {
  shelves.forEach((shelf) => {
    const numericId = Number(shelf?.id);

    if (Number.isFinite(numericId)) {
      _sid = Math.max(_sid, numericId);
    }
  });
};

const seedShelfIdCounterFromState = (state = {}) => {
  Object.values(state.fixtures || {}).forEach((fixture) => {
    seedShelfIdCounterFromShelves(fixture?.shelves || []);
  });
};

const nextShelfId = () => {
  _sid += 1;

  return _sid;
};

const normalizeShelfIds = (shelves = []) => {
  seedShelfIdCounterFromShelves(shelves);

  const usedIds = new Set();

  return shelves.filter(Boolean).map((shelf, index) => {
    let shelfId = shelf.id;

    if (!isUsableShelfId(shelfId) || usedIds.has(String(shelfId))) {
      shelfId = nextShelfId();
    }

    const normalizedId = String(shelfId);
    usedIds.add(normalizedId);

    return {
      ...shelf,
      id: normalizedId,
      number: index + 1,
      products: Array.isArray(shelf.products) ? shelf.products : [],
      depth: normalizeShelfDepth(shelf.depth),
    };
  });
};

const getShowPageScope = () => ShowPageScopeCollector.get([
  'previews',
  'layoutChanges',
  'getChangesLayoutById',
  'saveLayout',
  'updateLayoutChanges',
  'handleObjectMoving',
  'onClickDuplicateTemplateButton',
]);

export default {
  name: 'ShelfPlanogram',
  components: {
    ProductImageDropdown,
    TiltPicker,
    ShelfSkewPicker,
    ShelfDepthPicker,
    LogoUploader,
    InlineProductBrowser,
    EditLayout,
    LightboxModal,
  },
  props: {
    displayBuilderState: {
      type: String,
      default: '{}',
    },
  },
  data() {
    const savedState = normalizeDisplayBuilderState(this.parseDisplayBuilderState());
    seedShelfIdCounterFromState(savedState);
    const fixtures = savedState.fixtures || {};
    const fixtureOrder = getFixtureOrder(savedState);
    savedState.active_fixture = savedState.active_fixture || fixtureOrder[0] || null;

    const normalizeShelves = (shelves = []) => normalizeShelfIds(shelves).map((shelf) => ({
      ...shelf,
      products: Array.isArray(shelf.products)
        ? shelf.products.map((product) => ({
          ...product,
          tilt: Number(product.tilt || 0),
        }))
        : [],
    }));
    const createDefaultShelves = (count = DEFAULT_SHELF_COUNT) => {
      return Array.from({ length: count }, (_, index) => ({
        id: nextShelfId(),
        number: index + 1,
        products: [],
        depth: normalizeShelfDepth(),
      }));
    };
    const sections = {};

    fixtureOrder.forEach((fixtureKey) => {
      const savedShelves = normalizeShelves(fixtures[fixtureKey]?.shelves || []);
      const shelfCount = Number(fixtures[fixtureKey]?.shelfCount || savedShelves.length || DEFAULT_SHELF_COUNT);
      const shelves = savedShelves.length
        ? savedShelves
        : createDefaultShelves(shelfCount);

      sections[fixtureKey] = {
        shelves,
      };

      fixtures[fixtureKey].shelves = shelves;
      fixtures[fixtureKey].shelfCount = shelves.length;
    });

    return {
      sections,
      state: savedState,
      searchActive: false,
      searchQuery: '',
      fileNameIds: '',
      activeShelfId: null,
      activeShelfFixture: null,
      activeProductPickerShelfId: null,
      inlineProductPickerAnchor: null,
      inlineProductPickerStyle: {},
      isAutoFillProductPickerOpen: false,
      autoFillProductPickerAnchor: null,
      autoFillProductPickerStyle: {},
      isAutoFillingShelves: false,
      tabs: fixtureOrder.map((key) => ({
        key,
        label: fixtures[key]?.label || key,
      })),
      isHydratingDisplayBuilder: false,
      layoutEditSnapshot: null,
      syncShelvesTimer: null,
      syncShelvesRetryTimer: null,
      selectedInstanceVersion: 0,
    };
  },

  computed: {
    ...mapGetters('displayBuilder', ['isEditMode']),
    currentSection() {
      return this.sections[this.activeFixture] || { shelves: [] };
    },

    currentShelves() {
      return this.currentSection.shelves;
    },

    filteredShelves() {
      const query = String(this.searchQuery || '').trim().toLowerCase();

      if (!query) {
        return this.currentShelves;
      }

      return this.currentShelves
        .map((shelf) => ({
          ...shelf,
          products: (shelf.products || []).filter((product) => {
            const name = String(product.name || '').toLowerCase();
            const fileName = String(product.fileName || '').toLowerCase();
            const sku = String(product.sku || '').toLowerCase();

            return name.includes(query)
              || fileName.includes(query)
              || sku.includes(query);
          }),
        }))
        .filter((shelf) => shelf.products.length > 0);
    },
    selectedInstance() {
      void this.selectedInstanceVersion;

      return this.getSelectedInstance();
    },
    hasPlanogramProducts() {
      return this.getAllPlanogramShelves().some(({ shelf }) => {
        return this.shelfHasProducts(shelf);
      });
    },
    logos() {
      return this.state.logos || [];
    },
    canvasProductSpacing() {
      return normalizeShelfProductSpacing(this.state.productSpacing, 0);
    },
    activeFixture: {
      get() {
        return this.state.active_fixture || this.getFixtureKeys()[0] || null;
      },

      set(value) {
        if (!value) {
          return;
        }

        this.$set(this.state, 'active_fixture', value);

        if (!window.displayBuilderState) {
          window.displayBuilderState = {};
        }

        window.displayBuilderState.active_fixture = value;
      },
    },
  },

  watch: {
    searchActive(val) {
      if (val) {
        this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus());
      }
    },

    isEditMode(value) {
      if (value) {
        this.beginLayoutEdit();
        this.setBackgroundObjectsLockState?.(false);
      } else {
        window.displayBuilderIsLayoutEditing = false;
        window.displayBuilderHelper?.updateState(window.displayBuilderState || this.state);
        this.setBackgroundObjectsLockState?.(true);
      }
    },
  },
  mounted() {
    window.displayBuilderPanel = this;
    window.displayBuilderState = this.state;
    window.displayBuilderIsLayoutEditing = !!this.isEditMode;

    window.addEventListener('display-builder:fixture-selected', this.onFixtureSelected);
    window.addEventListener('display-builder:fixture-moved', this.onFixtureMoved);
    window.addEventListener('display-builder:shelf-selected', this.onShelfSelected);
    window.addEventListener('display-builder:selection-cleared', this.onSelectionCleared);
    window.addEventListener('display-builder:shelf-moved', this.onShelfMoved);
    window.addEventListener('display-builder:product-assigned-to-shelf', this.onProductAssignedToShelf);
    window.addEventListener('display-builder:inline-product-picker-ready', this.onInlineProductPickerReady);
    window.addEventListener('display-builder:helper-ready', this.onDisplayBuilderHelperReady);
    window.addEventListener('resize', this.onInlineProductViewportChange);
    window.addEventListener('scroll', this.onInlineProductViewportChange, true);
    document.addEventListener('click', this.onGlobalShelfAlignClick);
    document.addEventListener('click', this.onInlineProductDocumentClick);
    window.addEventListener('display-builder:logo-added', this.onDisplayBuilderLogoAdded);
    window.addEventListener(
      'display-builder:canvas-logo-deleted',
      this.onCanvasLogoDeleted
    );
    window.addEventListener('display-builder:shelf-load-complete', this.onShelfLoadCompleted);

    this.$nextTick(() => {
      this.syncShelvesToCanvas();
      this.syncCanvasProductSpacingControls();
      this.setBackgroundObjectsLockState(!this.isEditMode);

      setTimeout(() => {
        window.displayBuilderHelper?.restoreProductAssignmentsFromState?.();
      }, 500);
    });
    window.addEventListener(
      'display-builder:canvas-product-deleted',
      this.onCanvasProductDeleted
    );
  },

  beforeDestroy() {
    if (window.displayBuilderPanel === this) {
      window.displayBuilderPanel = null;
    }

    window.removeEventListener('display-builder:fixture-selected', this.onFixtureSelected);
    window.removeEventListener('display-builder:fixture-moved', this.onFixtureMoved);
    window.removeEventListener('display-builder:shelf-selected', this.onShelfSelected);
    window.removeEventListener('display-builder:selection-cleared', this.onSelectionCleared);
    window.removeEventListener('display-builder:shelf-moved', this.onShelfMoved);
    window.removeEventListener('display-builder:product-assigned-to-shelf', this.onProductAssignedToShelf);
    window.removeEventListener('display-builder:inline-product-picker-ready', this.onInlineProductPickerReady);
    window.removeEventListener('display-builder:helper-ready', this.onDisplayBuilderHelperReady);
    window.removeEventListener('resize', this.onInlineProductViewportChange);
    window.removeEventListener('scroll', this.onInlineProductViewportChange, true);
    document.removeEventListener('click', this.onGlobalShelfAlignClick);
    document.removeEventListener('click', this.onInlineProductDocumentClick);
    window.removeEventListener('display-builder:logo-added', this.onDisplayBuilderLogoAdded);
    window.removeEventListener('display-builder:shelf-load-complete', this.onShelfLoadCompleted);

    window.removeEventListener(
      'display-builder:canvas-product-deleted',
      this.onCanvasProductDeleted
    );
    window.removeEventListener(
      'display-builder:canvas-logo-deleted',
      this.onCanvasLogoDeleted
    );
  },

  methods: {
    refreshSelectedInstance() {
      this.selectedInstanceVersion += 1;
    },
    getSelectedInstance() {
      const helperCanvas = window.displayBuilderHelper?.canvas
        || window.displayBuilderFabricHelper?.canvas
        || null;
      const { previews = [] } = getShowPageScope() || {};
      const previewList = [
        ...(Array.isArray(window.previews) ? window.previews : []),
        ...(Array.isArray(previews) ? previews : []),
      ];
      const storeSelectedInstance = this.$store?.getters?.['layout/selectedInstance'];
      const candidateIds = [
        helperCanvas?.instance_id,
        helperCanvas?.instanceId,
        storeSelectedInstance?.id,
        window.displayBuilderInstanceId,
      ].filter(Boolean).map(String);
      const matchedPreview = previewList.find((preview) => preview?.canvas && preview.canvas === helperCanvas)
        || previewList.find((preview) => {
        return candidateIds.includes(String(preview?.id || ''));
      });

      if (matchedPreview) {
        return matchedPreview;
      }

      if (helperCanvas) {
        return {
          id: helperCanvas.instance_id
            || helperCanvas.instanceId
            || window.displayBuilderInstanceId
            || storeSelectedInstance?.id
            || null,
          canvas: helperCanvas,
        };
      }

      return storeSelectedInstance || previewList[0] || null;
    },
    getDisplayBuilderCanvas() {
      return this.getSelectedInstance()?.canvas
        || window.displayBuilderHelper?.canvas
        || window.displayBuilderFabricHelper?.canvas
        || null;
    },
    onDisplayBuilderHelperReady() {
      this.refreshSelectedInstance();
      this.$nextTick(() => {
        this.restoreProductsOnCurrentCanvas();
        this.syncCanvasProductSpacingControls();
      });
    },
    setActiveFixture(fixtureKey) {
      if (!fixtureKey) {
        return;
      }

      this.activeFixture = fixtureKey;
    },
    syncCurrentLayoutToCanvas() {
      this.syncShelvesToCanvas();
      this.markDisplayBuilderDirty();
    },

    positionShelfProducts(shelf) {
      const fixtureKey = this.getFixtureKeyForShelf(shelf);

      if (!fixtureKey || !shelf?.id) {
        return;
      }

      window.displayBuilderHelper?.positionProductsForShelf?.(
        fixtureKey,
        shelf.id
      );

      window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
      this.getDisplayBuilderCanvas()?.requestRenderAll?.();
    },
    shelvesForTab(tab) {
      return this.sections[tab]?.shelves || [];
    },
    isShelfActive(shelf) {
      return String(this.activeShelfId || '') === String(shelf?.id || '')
        && String(this.activeShelfFixture || '') === String(this.getFixtureKeyForShelf(shelf));
    },
    selectShelf(shelf) {
      if (!shelf?.id) {
        return;
      }

      const fixtureKey = this.getFixtureKeyForShelf(shelf);

      this.activeShelfId = shelf.id;
      this.activeShelfFixture = fixtureKey;
      this.setActiveFixture(fixtureKey);
      window.displayBuilderHelper?.setActiveShelf?.(fixtureKey, shelf.id);
    },
    getFixtureKeys() {
      return this.tabs.map((tab) => tab.key);
    },
    createEmptyShelves(count) {
      return Array.from({ length: Math.max(0, Number(count || 0)) }, (_, index) => ({
        id: nextShelfId(),
        number: index + 1,
        products: [],
        depth: normalizeShelfDepth(),
      }));
    },
    resizeShelves(shelves = [], count = 0) {
      const nextShelves = normalizeShelfIds(shelves).map((shelf, index) => ({
        ...shelf,
        number: index + 1,
        products: Array.isArray(shelf.products) ? shelf.products : [],
      }));

      while (nextShelves.length < count) {
        nextShelves.push({
          id: nextShelfId(),
          number: nextShelves.length + 1,
          products: [],
          depth: normalizeShelfDepth(),
        });
      }

      return nextShelves.slice(0, count).map((shelf, index) => ({
        ...shelf,
        number: index + 1,
      }));
    },
    beginLayoutEdit() {
      this.layoutEditSnapshot = cloneDisplayBuilderValue(this.buildDisplayBuilderStateFromPanel());
      window.displayBuilderIsLayoutEditing = true;
      window.displayBuilderHelper?.updateState(window.displayBuilderState || this.state);
    },
    moveLayoutShelf({ fixtureKey, shelfId, direction }) {
      const fixture = this.state.fixtures?.[fixtureKey];
      if (!fixture) return;

      const shelves = swapShelfWithNeighbour(
        this.sections[fixtureKey]?.shelves || fixture.shelves || [],
        fixture.rect,
        shelfId,
        direction
      );
      if (!shelves) return;

      this.sections = { ...this.sections, [fixtureKey]: { ...this.sections[fixtureKey], shelves } };
      this.state = {
        ...this.state,
        active_fixture: fixtureKey,
        fixtures: { ...this.state.fixtures, [fixtureKey]: { ...fixture, shelves } },
      };
      window.displayBuilderState = this.state;
      window.displayBuilderHelper?.updateState(this.state);
      this.selectShelf(shelves.find((shelf) => String(shelf.id) === String(shelfId)));
      this.markDisplayBuilderDirty();
    },
    applyLayoutDraft(draftFixtures = []) {
      const fixture_order = draftFixtures.map((fixture) => fixture.key);
      const previousSections = this.sections || {};
      const previousFixtures = this.state.fixtures || {};
      const removedFixtureKeys = this.getFixtureKeys().filter((fixtureKey) => {
        return !fixture_order.includes(fixtureKey);
      });
      const removedShelves = draftFixtures.flatMap((fixture) => {
        const previousFixture = previousFixtures[fixture.key] || {};
        const previousShelves = previousSections[fixture.key]?.shelves
          || previousFixture.shelves
          || [];
        const nextShelfCount = Number(fixture.shelfCount || 1);

        return previousShelves.slice(nextShelfCount);
      });
      const nextSections = {};
      const nextFixtures = {};

      this.removeProductsForFixtures(removedFixtureKeys);
      this.removeProductsForShelves(removedShelves);

      draftFixtures.forEach((fixture) => {
        const previousFixture = previousFixtures[fixture.key] || {};
        const previousRect = previousFixture.rect || fixture.rect;
        const previousShelves = previousSections[fixture.key]?.shelves || previousFixture.shelves || [];
        const nextShelfCount = Number(fixture.shelfCount || 1);
        const resizedShelves = this.resizeShelves(
          previousShelves,
          nextShelfCount
        );
        const previousShelfCount = Number(
          previousFixture.shelfCount || previousShelves.length || 0
        );
        const shelves = previousShelfCount !== nextShelfCount
          ? fitShelvesVerticallyToFixture(resizedShelves, fixture.rect)
          : transformShelvesWithFixture(resizedShelves, previousRect, fixture.rect);
        const tunedShelves = shelves.map((shelf, shelfIndex) => {
          const tuning = (fixture.shelves || []).find((item) => {
            return String(item.id) === String(shelf.id);
          }) || fixture.shelves?.[shelfIndex] || {};
          const productSpacing = Object.prototype.hasOwnProperty.call(tuning, 'productSpacing')
            ? tuning.productSpacing
            : shelf.productSpacing;

          return {
            ...shelf,
            height: previousShelfCount === nextShelfCount
              ? Number(tuning.height ?? shelf.height ?? 1)
              : Number(shelf.height || 1),
            yOffset: Number(tuning.yOffset ?? shelf.yOffset ?? 0),
            skew: Number(tuning.skew ?? shelf.skew ?? 0),
            productSpacing: normalizeShelfProductSpacing(productSpacing),
            depth: normalizeShelfDepth(tuning.depth ?? shelf.depth),
          };
        });

        nextSections[fixture.key] = { shelves: tunedShelves };
        nextFixtures[fixture.key] = {
          ...previousFixture,
          label: fixture.label,
          rect: fixture.rect,
          corners: fixture.corners,
          angle: Number(fixture.angle || 0),
          shelfCount: Number(fixture.shelfCount || tunedShelves.length || 1),
          shelves: tunedShelves,
        };
      });

      const active_fixture = fixture_order.includes(this.activeFixture)
        ? this.activeFixture
        : fixture_order[0] || null;

      this.sections = nextSections;
      this.tabs = draftFixtures.map((fixture, index) => ({
        key: fixture.key,
        label: fixture.label || `Fixture ${index + 1}`,
      }));
      this.state = {
        ...this.state,
        active_fixture,
        fixture_order,
        fixtures: nextFixtures,
      };

      this.setActiveFixture(active_fixture);

      window.displayBuilderState = this.state;
      window.displayBuilderHelper?.updateState(this.state);
      this.markDisplayBuilderDirty();
    },
    removeProductsForFixtures(fixtureKeys = []) {
      if (!fixtureKeys.length) {
        return;
      }

      const shelves = fixtureKeys.flatMap((fixtureKey) => {
        return this.sections[fixtureKey]?.shelves || [];
      });

      this.removeProductsForShelves(shelves);
    },
    removeProductsForShelves(shelves = []) {
      const canvasObjectIds = this.sortCanvasObjectIdsForRemoval(
        shelves.flatMap((shelf) => this.getShelfCanvasObjectIds(shelf))
      );

      if (!canvasObjectIds.length) {
        return;
      }

      canvasObjectIds.forEach((canvasObjectId) => {
        this.removeFacingObjectFromCanvas(canvasObjectId);
      });

      // Product selection is shared by the legacy image picker. Once a
      // product-filled fixture or shelf is removed, its picks must not survive
      // into a later manual Add Product operation.
      this.$store.dispatch('fileBrowserProductSelector/clearPickedItems');
    },
    restoreDisplayBuilderState(state) {
      const normalized = normalizeDisplayBuilderState(state || defaultFixtureState());
      const fixtureOrder = getFixtureOrder(normalized);
      const nextSections = {};

      seedShelfIdCounterFromState(normalized);

      fixtureOrder.forEach((fixtureKey) => {
        const fixture = normalized.fixtures[fixtureKey] || {};
        const restoredShelves = Array.isArray(fixture.shelves) && fixture.shelves.length
          ? normalizeShelfIds(fixture.shelves)
          : this.createEmptyShelves(Number(fixture.shelfCount || DEFAULT_SHELF_COUNT));
        const shelves = transformShelvesWithFixture(
          restoredShelves,
          fixture.rect,
          fixture.rect
        );

        nextSections[fixtureKey] = { shelves };
        normalized.fixtures[fixtureKey].shelves = shelves;
      });

      normalized.active_fixture = normalized.active_fixture || fixtureOrder[0] || null;
      this.state = normalized;
      this.sections = nextSections;
      this.tabs = fixtureOrder.map((key) => ({
        key,
        label: normalized.fixtures[key]?.label || key,
      }));

      window.displayBuilderState = normalized;
      window.displayBuilderHelper?.updateState(normalized);
      this.$nextTick(() => {
        this.restoreProductsOnCurrentCanvas();
        this.syncCanvasProductSpacingControls();
      });
    },
    cancelLayoutEdit() {
      if (!this.layoutEditSnapshot) {
        return;
      }

      this.restoreDisplayBuilderState(this.layoutEditSnapshot);
      this.layoutEditSnapshot = null;
      window.displayBuilderIsLayoutEditing = false;
    },
    finishLayoutEdit() {
      this.layoutEditSnapshot = null;
      window.displayBuilderIsLayoutEditing = false;
    },
    async commitLayoutEdit(options = {}) {
      this.markDisplayBuilderDirty();
      this.markDisplayBuilderDirty.flush?.();
      this.layoutEditSnapshot = null;
      window.displayBuilderIsLayoutEditing = false;

      if (options.skipSave) {
        return;
      }

      const { saveLayout } = getShowPageScope();

      if (saveLayout) {
        await saveLayout();
      }
    },

    totalFacings(shelf) {
      return shelf.products.reduce((sum, p) => sum + this.getProductFacingCount(p), 0);
    },

    productMatches(product) {
      const query = String(this.searchQuery || '').trim().toLowerCase();

      if (!query || !product) {
        return false;
      }

      const name = String(product.name || '').toLowerCase();
      const fileName = String(product.fileName || '').toLowerCase();
      const sku = String(product.sku || '').toLowerCase();

      return name.includes(query)
        || fileName.includes(query)
        || sku.includes(query);
    },

    async increment(shelf, product) {
      await this.duplicateProductForShelf(shelf, product);
    },

    async decrement(shelf, product) {
      if (this.getProductFacingCount(product) <= 1) {
        return;
      }

      if (!Array.isArray(product.facing_object_ids)) {
        this.$set(product, 'facing_object_ids', []);
      }

      let canvasObjectId = product.facing_object_ids.pop();

      if (!canvasObjectId) {
        canvasObjectId = this.findUntrackedAdditionalProductObjectId(shelf, product);
      }

      if (!canvasObjectId) {
        this.$set(product, 'facings', 1);
      } else {
        product.facing_object_ids = product.facing_object_ids.filter((id) => {
          return String(id) !== String(canvasObjectId);
        });
        await this.removeFacingObjectFromCanvas(canvasObjectId);
        this.updateProductFacingCount(product);
      }

      this.markDisplayBuilderDirty();
      this.markDisplayBuilderDirty.flush?.();
      this.$emit('facing-change', { shelf, product });

      this.$nextTick(() => {
        this.positionShelfProducts(shelf);
      });
    },

    async removeProduct(shelf, product) {
      if (!shelf || !product) {
        return;
      }

      const result = await isDeleteConfirmed(
        false,
        'Are you sure you want to remove this product from the shelf?'
      );

      if (!result) {
        return;
      }

      const facingObjectIdsToRemove = Array.isArray(product.facing_object_ids)
        ? product.facing_object_ids.map(String)
        : [];
      const seenMainObjectIds = new Set();
      const mainObjectIdsToRemove = [
        product.canvas_object_id,
        product.objectId,
      ]
        .filter(Boolean)
        .map(String)
        .filter((id) => {
          if (seenMainObjectIds.has(id)) {
            return false;
          }

          seenMainObjectIds.add(id);
          return true;
        })
        .filter((id) => !facingObjectIdsToRemove.includes(id));

      const canvasObjectIdsToRemove = this.sortCanvasObjectIdsForRemoval([
        ...facingObjectIdsToRemove,
        ...mainObjectIdsToRemove,
      ]);

      for (const canvasObjectId of canvasObjectIdsToRemove) {
        await this.removeFacingObjectFromCanvas(canvasObjectId);
      }

      // Remove product row from panel
      const idx = shelf.products.findIndex((p) => {
        return String(p.id) === String(product.id);
      });

      if (idx !== -1) {
        shelf.products.splice(idx, 1);
      }

      product.facing_object_ids = [];
      product.facings = 1;

      this.markDisplayBuilderDirty();
      this.markDisplayBuilderDirty.flush?.();

      this.$nextTick(() => {
        const fixtureKey = this.getFixtureKeyForShelf(shelf);

        window.displayBuilderHelper?.positionProductsForShelf?.(
          fixtureKey,
          shelf.id
        );
        window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
        this.getDisplayBuilderCanvas()?.requestRenderAll?.();
      });
    },

    setActiveObject (field) {
      const canvas = this.getDisplayBuilderCanvas();

      if (!canvas) {
        return false;
      }

      let target = _.find(canvas.getObjects(), ['id', field.objectId])
      if (!target) {
        return false;
      }
      this.fileNameIds = field.fileName;

      window.isFieldsObject = true;
      return this.setActiveFabricObject(canvas, target);
    },

    async setActiveFabricObject(canvas, target) {
      if (!canvas || !target) {
        return false;
      }

      canvas.discardActiveObject();

      target.set({
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,

        lockMovementX: false,
        lockMovementY: false,
        lockScalingX: false,
        lockScalingY: false,
        lockRotation: false,

        transparentCorners: false,
      });

      target.moveTo(9800);

      target.setCoords();

      canvas.setActiveObject(target);

      canvas.requestRenderAll();
      return true;
    },

    async addProduct(shelf, event = null) {
      this.refreshSelectedInstance();
      this.closeAutoFillProductPicker();
      this.selectShelf(shelf);
      this.activeProductPickerShelfId = shelf.id;
      this.inlineProductPickerAnchor = event?.currentTarget || null;
      this.positionInlineProductPicker();

      const fixtureKey = this.activeFixture
        || window.displayBuilderState.active_fixture
        || this.getFixtureKeys()[0]
        || 'center';

      window.displayBuilderHelper?.setPendingShelfTarget(
        fixtureKey,
        shelf.id
      );

      const selectedInstance = this.getSelectedInstance();

      if (!selectedInstance?.canvas || !selectedInstance?.id) {
        this.$nextTick(() => {
          this.getInlineProductBrowser()?.open();
          this.getInlineProductBrowser()?.setError('Canvas is still loading');
        });
        return;
      }

      selectedInstance.canvas.fire('add:product-images', {
        instanceId: selectedInstance.id,
        isNeedAddProductDimensionsField: false,
        showModal: false,
        addItemsMode: true,
      });

      this.$nextTick(() => {
        this.getInlineProductBrowser()?.open();
        this.positionInlineProductPicker();
        this.getInlineProductBrowser()?.focusInlineProductSearch();
      });
    },
    async openAutoFillProductBrowser(event = null) {
      this.refreshSelectedInstance();
      this.closeInlineProductPicker();
      this.isAutoFillProductPickerOpen = true;
      this.autoFillProductPickerAnchor = event?.currentTarget || null;
      this.positionAutoFillProductPicker();

      const selectedInstance = this.getSelectedInstance();

      if (!selectedInstance?.canvas || !selectedInstance?.id) {
        this.$nextTick(() => {
          this.getAutoFillProductBrowser()?.open();
          this.getAutoFillProductBrowser()?.setError('Canvas is still loading');
        });
        return;
      }

      await this.$store.dispatch('fileBrowserProductSelector/setSearchQuery', '');
      await this.$store.dispatch('fileBrowserProductSelector/enableBrowserMode');

      this.$nextTick(() => {
        this.getAutoFillProductBrowser()?.open();
        this.positionAutoFillProductPicker();
        this.getAutoFillProductBrowser()?.focusInlineProductSearch();
      });
    },

    onInlineProductPickerReady(event) {
      const instanceId = event.detail?.instanceId;
      const selectedInstance = this.getSelectedInstance();

      if (String(instanceId) !== String(selectedInstance?.id)) {
        return;
      }

      this.getInlineProductBrowser()?.setPickerInstanceId(instanceId);
      this.$nextTick(() => {
        this.positionInlineProductPicker();
        this.getInlineProductBrowser()?.focusInlineProductSearch();
      });
    },

    isInlineProductPickerOpen(shelf) {
      return String(this.activeProductPickerShelfId || '') === String(shelf?.id || '');
    },

    onInlineProductDocumentClick(event) {
      if (event.target.closest('.sp-inline-picker') || event.target.closest('.sp-add-product') || event.target.closest('.sp-auto-fill-trigger')) {
        return;
      }

      if (this.activeProductPickerShelfId) {
        this.closeInlineProductPicker();
      }

      if (this.isAutoFillProductPickerOpen) {
        this.closeAutoFillProductPicker();
      }
    },

    onInlineProductViewportChange() {
      if (this.activeProductPickerShelfId) {
        this.positionInlineProductPicker();
      }

      if (this.isAutoFillProductPickerOpen) {
        this.positionAutoFillProductPicker();
      }
    },

    positionInlineProductPicker() {
      const anchor = this.inlineProductPickerAnchor;

      if (!anchor?.getBoundingClientRect) {
        this.inlineProductPickerStyle = {};
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const gutter = 12;
      const preferredWidth = Math.max(rect.width, 320);
      const width = Math.min(preferredWidth, 520, Math.max(260, viewportWidth - gutter * 2));
      const left = Math.min(
        Math.max(gutter, rect.left),
        Math.max(gutter, viewportWidth - width - gutter)
      );
      const belowTop = rect.bottom + 8;
      const availableBelow = viewportHeight - belowTop - gutter;
      const availableAbove = rect.top - gutter;
      const openAbove = availableBelow < 260 && availableAbove > availableBelow;
      const maxHeight = Math.max(
        220,
        Math.min(340, openAbove ? availableAbove - 8 : availableBelow)
      );
      const top = openAbove
        ? Math.max(gutter, rect.top - maxHeight - 8)
        : belowTop;

      this.inlineProductPickerStyle = {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        maxHeight: `${maxHeight}px`,
      };
    },

    getInlineProductBrowser() {
      const browser = this.$refs.inlineProductBrowser;

      return Array.isArray(browser) ? browser[0] : browser;
    },

    closeInlineProductPicker(clearPendingTarget = true) {
      const browser = this.getInlineProductBrowser();

      if (browser) {
        browser.closeInlineProductPicker(clearPendingTarget);
        return;
      }

      this.onInlineProductBrowserClose({ clearPendingTarget });
    },

    onInlineProductBrowserClose(payload = {}) {
      this.activeProductPickerShelfId = null;
      this.inlineProductPickerAnchor = null;
      this.inlineProductPickerStyle = {};

      if (payload.clearPendingTarget) {
        window.displayBuilderHelper?.clearPendingShelfTarget?.();
      }
    },
    positionAutoFillProductPicker() {
      const anchor = this.autoFillProductPickerAnchor;

      if (!anchor?.getBoundingClientRect) {
        this.autoFillProductPickerStyle = {};
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const gutter = 8;
      const width = Math.min(320, Math.max(280, viewportWidth - gutter * 2));
      const left = Math.min(
        Math.max(gutter, rect.right - width),
        Math.max(gutter, viewportWidth - width - gutter)
      );
      const belowTop = rect.bottom + 8;
      const availableBelow = viewportHeight - belowTop - gutter;
      const availableAbove = rect.top - gutter;
      const openAbove = availableBelow < 380 && availableAbove > availableBelow;
      const maxHeight = Math.max(
        320,
        Math.min(520, openAbove ? availableAbove - 8 : availableBelow)
      );
      const top = openAbove
        ? Math.max(gutter, rect.top - maxHeight - 8)
        : belowTop;

      this.autoFillProductPickerStyle = {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        maxHeight: `${maxHeight}px`,
      };
    },
    getAutoFillProductBrowser() {
      const browser = this.$refs.autoFillProductBrowser;

      return Array.isArray(browser) ? browser[0] : browser;
    },
    closeAutoFillProductPicker() {
      const browser = this.getAutoFillProductBrowser();

      if (browser && this.isAutoFillingShelves) {
        return;
      }

      if (browser) {
        browser.closeInlineProductPicker();
        return;
      }

      this.onAutoFillProductBrowserClose();
    },
    onAutoFillProductBrowserClose() {
      if (this.isAutoFillingShelves) {
        return;
      }

      this.isAutoFillProductPickerOpen = false;
      this.autoFillProductPickerAnchor = null;
      this.autoFillProductPickerStyle = {};
      window.displayBuilderHelper?.clearPendingShelfTarget?.();
    },

    getShelfCanvasObjectIds(shelf) {
      const fixtureKey = this.getFixtureKeyForShelf(shelf);
      const productObjectIds = (shelf?.products || [])
        .flatMap((product) => this.getProductCanvasObjectIds(product));
      const canvasObjectIds = (window.displayBuilderHelper?.findProductObjectsForShelf?.(
        fixtureKey,
        shelf?.id
      ) || []).map((object) => object.id);

      return [...new Set([...productObjectIds, ...canvasObjectIds].filter(Boolean).map(String))];
    },
    sortCanvasObjectIdsForRemoval(canvasObjectIds = []) {
      const uniqueObjectIds = [...new Set(canvasObjectIds.filter(Boolean).map(String))];
      const imageObjectIds = uniqueObjectIds
        .filter((id) => id.startsWith(IMAGE_PREFIX_ID))
        .sort((a, b) => {
          const aIndex = parseInt(a.replace(IMAGE_PREFIX_ID, ''), 10);
          const bIndex = parseInt(b.replace(IMAGE_PREFIX_ID, ''), 10);

          return bIndex - aIndex;
        });
      const otherObjectIds = uniqueObjectIds.filter((id) => {
        return !id.startsWith(IMAGE_PREFIX_ID);
      });

      return [...imageObjectIds, ...otherObjectIds];
    },
    removeRejectedProductObjects(objects = []) {
      this.sortCanvasObjectIdsForRemoval(
        objects.map((object) => object?.id).filter(Boolean)
      ).forEach((objectId) => {
        this.removeFacingObjectFromCanvas(objectId);
      });
    },
    async removeShelfProductsFromCanvas(shelf) {
      const canvasObjectIds = this.sortCanvasObjectIdsForRemoval(
        this.getShelfCanvasObjectIds(shelf)
      );

      for (const canvasObjectId of canvasObjectIds) {
        await this.removeFacingObjectFromCanvas(canvasObjectId);
      }
    },
    getAllPlanogramShelves() {
      return this.getFixtureKeys().flatMap((fixtureKey) => {
        return (this.sections[fixtureKey]?.shelves || []).map((shelf) => ({
          fixtureKey,
          shelf,
        }));
      });
    },
    shelfHasProducts(shelf) {
      return Boolean((shelf?.products || []).length);
    },
    getShelfAutoFillRect(shelf, fixtureKey = null) {
      const resolvedFixtureKey = fixtureKey || this.getFixtureKeyForShelf(shelf);
      const existingShelf = this.state.fixtures?.[resolvedFixtureKey]?.shelves?.find((item) => {
        return String(item.id) === String(shelf?.id);
      });
      const fixtureRect = window.displayBuilderHelper?.rects?.[resolvedFixtureKey]
        || this.state.fixtures?.[resolvedFixtureKey]?.rect;

      const x = Number(shelf?.x ?? existingShelf?.x ?? 0);
      const y = Number(shelf?.y ?? existingShelf?.y ?? 0);
      const width = Number(shelf?.width ?? existingShelf?.width ?? fixtureRect?.width ?? 260);
      const height = Number(shelf?.height ?? existingShelf?.height ?? 85);

      return {
        x,
        y,
        width,
        height,
        centerX: x + width / 2,
        centerY: y + height / 2,
      };
    },
    getShelfAutoFillWidth(shelf, fixtureKey = null) {
      return this.getShelfAutoFillRect(shelf, fixtureKey).width;
    },
    getAutoFillFacingCount(shelf, fixtureKey) {
      const shelfWidth = this.getShelfAutoFillWidth(shelf, fixtureKey);
      const facingWidth = Math.max(1, DISPLAY_BUILDER_CANVAS_WIDTH * 0.065);

      return Math.max(4, Math.min(30, Math.floor(shelfWidth / facingWidth)));
    },
    getAutoFillProductFileId(item) {
      if (item?.product_type === 'salsify') {
        return String(item.fileId || '').startsWith('salsify:')
          ? item.fileId
          : `salsify:${item.id}`;
      }

      if (String(item?.fileId || '').startsWith('asin:')) {
        return item.fileId;
      }

      return this.normalizeProductFileName(item?.fileId || item?.name || item?.fileName || item?.id);
    },
    isTechnicalProductName(value) {
      const name = String(value || '').trim();

      return !name
        || name.startsWith('salsify:')
        || name.startsWith('asin:')
        || name.startsWith('bynder:')
        || name.startsWith('display-builder-autofill-facing-')
        || name.startsWith('display-builder-facing-');
    },
    getReadableProductName(item, fallback = '') {
      const candidates = [
        item?.product_name,
        item?.standart_title,
        item?.standard_title,
        item?.title,
        item?.label,
        item?.name,
      ];

      const readableName = candidates.find((candidate) => {
        return candidate && !this.isTechnicalProductName(candidate);
      });

      return readableName || fallback;
    },
    getSalsifyFileIdCandidates(item) {
      return [
        item?.fileId,
        item?.id ? `salsify:${item.id}` : '',
        item?.asset_id ? `salsify:${item.asset_id}` : '',
        item?.salsify_id ? `salsify:${item.salsify_id}` : '',
      ].filter(Boolean).map(String);
    },
    getStoredProductMetadataForFileId(fileId) {
      const metadataByFileId = window.displayBuilderProductMetadataByFileId || {};
      const normalizedFileId = this.normalizeProductFileName(fileId);

      return metadataByFileId[normalizedFileId] || null;
    },
    findPickedProductItemForFileId(fileId) {
      const normalizedFileId = this.normalizeProductFileName(fileId);

      if (!normalizedFileId) {
        return null;
      }

      const pickedItems = this.$store.getters['fileBrowserProductSelector/pickedItems'] || [];

      const pickedItem = pickedItems.find((item) => {
        const candidates = item?.product_type === 'salsify'
          ? this.getSalsifyFileIdCandidates(item)
          : [item?.fileId, item?.name, item?.fileName, item?.id].filter(Boolean).map(String);

        return candidates.some((candidate) => {
          return this.isSameProductFileName(candidate, normalizedFileId);
        });
      }) || null;

      return pickedItem || this.getStoredProductMetadataForFileId(normalizedFileId);
    },
    getShelfProductNameForObject(object, fallback = 'Product') {
      const fileId = object?.fileName || object?.fileId || fallback;
      const sourceItem = this.findPickedProductItemForFileId(fileId);
      const sourceName = this.getReadableProductName(sourceItem);

      if (sourceName) {
        return sourceName;
      }

      return this.getReadableProductName(object, fallback);
    },
    getShelfProductDisplayName(product) {
      return this.getReadableProductName(
        product,
        product?.fileName || 'Product'
      );
    },
    getAutoFillSourceItemsByFileId(items = []) {
      const sourceItems = new Map();

      items.forEach((item) => {
        const isSalsifyProduct = item?.product_type === 'salsify'
          || String(item?.fileId || '').startsWith('salsify:');

        if (
          item?.product_type !== DISPLAY_BUILDER_UPLOAD_PRODUCT_TYPE
          && !isSalsifyProduct
        ) {
          return;
        }

        const fileId = this.getAutoFillProductFileId(item);

        if (!fileId) {
          return;
        }

        sourceItems.set(String(fileId), {
          ...item,
          fileId,
          path: item?.path || item?.original_path || item?.full_url || item?.fileId || fileId,
          original_path: item?.original_path || item?.path || item?.full_url || item?.fileId || fileId,
          product_type: isSalsifyProduct ? 'salsify' : item?.product_type,
        });
      });

      return sourceItems;
    },
    async restoreAutoFillSourcePickedItems(fileIds = [], sourceItems = []) {
      const sourceItemsByFileId = this.getAutoFillSourceItemsByFileId(sourceItems);

      if (!sourceItemsByFileId.size) {
        return;
      }

      const pickedFileIds = new Set(
        (this.$store.getters['fileBrowserProductSelector/pickedItems'] || [])
          .map((item) => String(item?.fileId || ''))
          .filter(Boolean)
      );

      for (const [fileId, sourceItem] of sourceItemsByFileId.entries()) {
        if (!fileIds.includes(fileId) || pickedFileIds.has(fileId)) {
          continue;
        }

        await this.$store.dispatch('fileBrowserProductSelector/pickItem', {
          ...sourceItem,
          count: fileIds.filter((itemFileId) => itemFileId === fileId).length || 1,
        });
      }
    },
    getCurrentCanvasProductFileIds() {
      const canvas = this.getDisplayBuilderCanvas();

      if (!canvas) {
        return [];
      }

      return canvas.getObjects()
        .filter((object) => {
          return object?.fieldType === FIELD_TYPE_PRODUCT_IMAGE
            || String(object?.id || '').startsWith(IMAGE_PREFIX_ID);
        })
        .map((object) => this.normalizeProductFileName(object.fileName))
        .filter(Boolean);
    },
    getAutoFillBatchFileIds(items, count, startIndex = 0) {
      const facingCount = Math.max(0, Number(count) || 0);
      const seenFileIds = new Set();
      const fileIds = items
        .map((item) => this.getAutoFillProductFileId(item))
        .filter(Boolean)
        .filter((fileId) => {
          const normalizedFileId = this.normalizeProductFileName(fileId);

          if (!normalizedFileId || seenFileIds.has(normalizedFileId)) {
            return false;
          }

          seenFileIds.add(normalizedFileId);
          return true;
        });

      if (!fileIds.length || !facingCount) {
        return [];
      }

      const productCount = Math.min(facingCount >= 4 ? 2 : 1, fileIds.length);
      const selectedFileIds = Array.from({ length: productCount }, (_, index) => {
        return fileIds[(startIndex + index) % fileIds.length];
      });
      const baseRunLength = Math.floor(facingCount / productCount);
      const extraFacingCount = facingCount % productCount;

      return selectedFileIds.flatMap((fileId, index) => {
        const runLength = baseRunLength + (index < extraFacingCount ? 1 : 0);

        return Array.from({ length: runLength }, () => fileId);
      });
    },
    buildAutoFillShelfPlans(targetShelves = [], items = []) {
      const rowsByFixture = new Map();

      targetShelves.forEach(({ fixtureKey, shelf }, originalIndex) => {
        const rect = this.getShelfAutoFillRect(shelf, fixtureKey);
        const facingCount = this.getAutoFillFacingCount(shelf, fixtureKey);
        const tolerance = Math.max(12, rect.height * 0.55);

        if (!facingCount) {
          return;
        }

        if (!rowsByFixture.has(fixtureKey)) {
          rowsByFixture.set(fixtureKey, []);
        }

        const rows = rowsByFixture.get(fixtureKey);
        let row = rows.find((candidate) => {
          return Math.abs(candidate.centerY - rect.centerY) <= Math.max(candidate.tolerance, tolerance);
        });

        if (!row) {
          row = {
            fixtureKey,
            centerY: rect.centerY,
            tolerance,
            shelves: [],
          };
          rows.push(row);
        } else {
          const shelfCount = row.shelves.length;
          row.centerY = ((row.centerY * shelfCount) + rect.centerY) / (shelfCount + 1);
          row.tolerance = Math.max(row.tolerance, tolerance);
        }

        row.shelves.push({
          fixtureKey,
          shelf,
          rect,
          facingCount,
          originalIndex,
        });
      });

      const shelfPlans = [];
      let rowProductOffset = 0;

      rowsByFixture.forEach((rows) => {
        rows
          .sort((a, b) => a.centerY - b.centerY)
          .forEach((row) => {
            const shelves = row.shelves.sort((a, b) => {
              if (a.rect.centerX !== b.rect.centerX) {
                return a.rect.centerX - b.rect.centerX;
              }

              return a.originalIndex - b.originalIndex;
            });
            const rowFacingCount = shelves.reduce((sum, item) => {
              return sum + item.facingCount;
            }, 0);
            const rowFileIds = this.getAutoFillBatchFileIds(items, rowFacingCount, rowProductOffset);
            let cursor = 0;

            shelves.forEach((item) => {
              shelfPlans.push({
                fixtureKey: item.fixtureKey,
                shelf: item.shelf,
                fileIds: rowFileIds.slice(cursor, cursor + item.facingCount),
              });
              cursor += item.facingCount;
            });

            rowProductOffset += 1;
          });
      });

      return shelfPlans;
    },
    syncAutoFillLayoutState(layoutSnapshot = {}) {
      const currentState = this.buildDisplayBuilderStateFromPanel();
      const snapshotFixtures = layoutSnapshot.fixtures || {};
      const fixtures = {};

      this.getFixtureKeys().forEach((fixtureKey) => {
        const currentFixture = currentState.fixtures?.[fixtureKey] || {};
        const snapshotFixture = snapshotFixtures[fixtureKey] || {};

        fixtures[fixtureKey] = {
          ...currentFixture,
          rect: snapshotFixture.rect || currentFixture.rect,
          corners: snapshotFixture.corners || currentFixture.corners,
          angle: snapshotFixture.angle ?? currentFixture.angle,
        };
      });

      const state = normalizeDisplayBuilderState({
        ...currentState,
        active_fixture: layoutSnapshot.active_fixture || currentState.active_fixture,
        fixture_order: layoutSnapshot.fixture_order || currentState.fixture_order,
        fixtures,
      });

      this.state = state;
      window.displayBuilderState = state;
      window.displayBuilderHelper?.updateState(state);

      return state;
    },
    getAutoFillPrimaryFileIds(fileIds = []) {
      const seen = new Set();

      return fileIds.filter((fileId) => {
        const normalizedFileId = this.normalizeProductFileName(fileId);

        if (!normalizedFileId || seen.has(normalizedFileId)) {
          return false;
        }

        seen.add(normalizedFileId);
        return true;
      });
    },
    waitForAutoFillCanvasObjects(expectedCount) {
      return new Promise((resolve) => {
        const startedAt = Date.now();
        const check = () => {
          const count = this.getAssignableProductObjects().length;

          if (count >= expectedCount) {
            resolve(true);
            return;
          }

          if (Date.now() - startedAt > 30000) {
            resolve(false);
            return;
          }

          setTimeout(check, 250);
        };

        check();
      });
    },
    waitForAutoFillSave(canvas, requestId) {
      return new Promise((resolve, reject) => {
        const eventName = 'product-images-selector:auto-fill-save-complete';
        const timeout = setTimeout(() => {
          canvas.off(eventName, onComplete);
          reject(new Error('Timed out while drawing Auto Fill product images.'));
        }, 30000);
        const onComplete = ({ requestId: completedRequestId, error } = {}) => {
          if (completedRequestId !== requestId) {
            return;
          }

          clearTimeout(timeout);
          canvas.off(eventName, onComplete);

          if (error) {
            reject(new Error(error));
            return;
          }

          resolve();
        };

        canvas.on(eventName, onComplete);
      });
    },
    sortAutoFillProductObjects(objects = []) {
      return [...objects].sort((a, b) => {
        const aIndex = parseInt(String(a.id || '').replace(IMAGE_PREFIX_ID, ''), 10);
        const bIndex = parseInt(String(b.id || '').replace(IMAGE_PREFIX_ID, ''), 10);

        if (Number.isFinite(aIndex) && Number.isFinite(bIndex)) {
          return aIndex - bIndex;
        }

        return String(a.id || '').localeCompare(String(b.id || ''));
      });
    },
    takeAutoFillProductObject(objects, fileId) {
      const normalizedFileId = this.normalizeProductFileName(fileId);
      const matchIndex = objects.findIndex((object) => {
        return this.isSameProductFileName(object.fileName, normalizedFileId);
      });

      if (matchIndex === -1) {
        return null;
      }

      return objects.splice(matchIndex, 1)[0] || null;
    },
    findAutoFillSourceObject(productObjects, fileId) {
      const normalizedFileId = this.normalizeProductFileName(fileId);

      return productObjects.find((object) => {
        return this.isSameProductFileName(object.fileName, normalizedFileId);
      }) || this.getAssignableProductObjects().find((object) => {
        return this.isSameProductFileName(object.fileName, normalizedFileId);
      }) || null;
    },
    createAutoFillFacingObjectId(fileId) {
      const safeFileId = String(fileId || 'product')
        .replace(/[^A-Za-z0-9_-]/g, '-')
        .slice(0, 60);

      return [
        'display-builder-autofill-facing',
        safeFileId || 'product',
        Date.now(),
        Math.random().toString(36).slice(2, 8),
      ].join('-');
    },
    buildAutoFillShelfProducts(objects = []) {
      const products = [];

      objects.forEach((object) => {
        const fileName = this.normalizeProductFileName(object?.fileName);
        const existingProduct = products.find((product) => {
          return this.isSameProductFileName(product.fileName, fileName);
        });

        if (existingProduct) {
          this.addProductObjectAsFacing(existingProduct, object, object.id);
          return;
        }

        const product = this.makeProductFromCanvasObject(object, object.id);
        const productName = this.getShelfProductNameForObject(object, product.name);

        product.fileName = object.fileName || product.fileName;
        product.name = productName || product.name;
        product.product_name = productName || product.product_name || product.name;
        products.push(product);
      });

      products.forEach((product) => {
        this.updateProductFacingCount(product);
      });

      return products;
    },
    async drawAutoFillProductObjects(fileIds, sourceItems = [], preserveExisting = false) {
      const selectedInstance = this.getSelectedInstance();

      if (!selectedInstance?.canvas || !selectedInstance?.id) {
        return [];
      }

      const existingFileIds = preserveExisting
        ? this.getCurrentCanvasProductFileIds()
        : [];
      const allFileIds = [...existingFileIds, ...fileIds];
      const beforeProductObjects = this.getAssignableProductObjects().map((object) => ({
        id: object.id,
        fileName: object.fileName || '',
        templateFieldId: object.templateFieldId || object.fieldId || null,
      }));

      await this.$store.dispatch('fileBrowserProductSelector/setCurrentPickedItems', []);
      await this.$store.dispatch('fileBrowserProductSelector/setCurrentPickedItems', allFileIds);
      await this.restoreAutoFillSourcePickedItems(allFileIds, sourceItems);

      window.displayBuilderHelper?.clearPendingShelfTarget?.();
      const requestId = `auto-fill-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const saveCompleted = this.waitForAutoFillSave(selectedInstance.canvas, requestId);

      selectedInstance.canvas.displayBuilderAutoFillRequest = {
        requestId,
        fileIds: [...fileIds],
      };

      selectedInstance.canvas.fire(
        'product-images-selector:save-images',
        selectedInstance.id
      );

      await saveCompleted;
      const didLoadExpectedObjects = await this.waitForAutoFillCanvasObjects(allFileIds.length);

      if (!didLoadExpectedObjects) {
        throw new Error('Timed out while drawing Auto Fill product images.');
      }

      const reconciledObjects = this.reconcilePendingProductObjects({
        beforeProductObjects,
      });

      const beforeIds = beforeProductObjects.map((object) => String(object.id));
      const newObjects = Array.isArray(reconciledObjects)
        ? reconciledObjects
        : this.getAssignableProductObjects().filter((object) => {
          return !beforeIds.includes(String(object.id));
        });

      return this.sortAutoFillProductObjects(newObjects);
    },
    async autoFillShelves({ mode = 'replace-all', items = [] } = {}) {
      const browser = this.getAutoFillProductBrowser();

      if (this.isAutoFillingShelves) {
        return;
      }

      if (!items.length) {
        browser?.setError('Select at least one product');
        browser?.finishAutoFill();
        return;
      }

      const selectedInstance = this.getSelectedInstance();

      if (!selectedInstance?.canvas || !selectedInstance?.id) {
        browser?.setError('Canvas is still loading');
        browser?.finishAutoFill();
        return;
      }

      this.isAutoFillingShelves = true;

      try {
        const layoutStateBeforeAutoFill = cloneDisplayBuilderValue(this.state);
        const allShelves = this.getAllPlanogramShelves();
        const targetShelves = mode === 'empty-only'
          ? allShelves.filter(({ shelf }) => !this.shelfHasProducts(shelf))
          : allShelves;
        if (!targetShelves.length) {
          browser?.setError('No shelves to fill');
          browser?.finishAutoFill();
          this.isAutoFillingShelves = false;
          return;
        }

        selectedInstance.canvas.renderLock?.add('display-builder:auto-fill');

        if (mode === 'replace-all') {
          for (const { shelf } of allShelves) {
            await this.removeShelfProductsFromCanvas(shelf);
            this.$set(shelf, 'products', []);
          }
        } else if (mode === 'empty-only') {
          for (const { shelf } of targetShelves) {
            await this.removeShelfProductsFromCanvas(shelf);
            this.$set(shelf, 'products', []);
          }
        }

        const shelfPlans = this.buildAutoFillShelfPlans(targetShelves, items);
        const productObjects = await this.drawAutoFillProductObjects(
          this.getAutoFillPrimaryFileIds(shelfPlans.flatMap((plan) => plan.fileIds)),
          items,
          mode === 'empty-only'
        );
        const autoFillSourceObjects = [...productObjects];

        // Drawing product-image fields can recreate canvas objects and briefly
        // expose the default fixture rects. Restore the configured geometry
        // before any Auto Fill object is assigned to a shelf.
        this.syncAutoFillLayoutState(layoutStateBeforeAutoFill);

        productObjects.forEach((object) => {
          object.set({ visible: false });
        });

        for (const { fixtureKey, shelf, fileIds } of shelfPlans) {
          const shelfObjects = [];
          const primaryObjectsByFileName = new Map();

          for (const fileId of fileIds) {
            const normalizedFileId = this.normalizeProductFileName(fileId);
            let object = primaryObjectsByFileName.get(normalizedFileId) || null;

            if (!object) {
              object = this.takeAutoFillProductObject(productObjects, fileId);

              if (object) {
                primaryObjectsByFileName.set(normalizedFileId, object);
              } else {
                const sourceObject = this.findAutoFillSourceObject(autoFillSourceObjects, fileId);

                if (!sourceObject) {
                  continue;
                }

                object = await this.cloneProductObjectForFacing(
                  sourceObject,
                  this.createAutoFillFacingObjectId(fileId)
                );

                selectedInstance.canvas.add(object);
                primaryObjectsByFileName.set(normalizedFileId, object);
              }
            } else {
              object = await this.cloneProductObjectForFacing(
                object,
                this.createAutoFillFacingObjectId(fileId)
              );

              selectedInstance.canvas.add(object);
            }

            window.displayBuilderHelper?.assignProductObjectToShelf?.(
              object,
              fixtureKey,
              shelf.id,
              { dispatchEvent: false }
            );
            object.set({ visible: true });
            shelfObjects.push(object);
          }

          this.$set(shelf, 'products', this.buildAutoFillShelfProducts(shelfObjects));
          this.positionShelfProducts(shelf);
        }

        window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
        this.markDisplayBuilderDirty();
        this.markDisplayBuilderDirty.flush?.();
        if (mode === 'empty-only') {
          window.displayBuilderHelper?.restoreProductAssignmentsFromState?.();
        }
        selectedInstance.canvas.renderLock?.remove('display-builder:auto-fill');
        this.syncAutoFillLayoutState(layoutStateBeforeAutoFill);
        window.displayBuilderHelper?.reflowAllShelfProducts?.({ forceWarp: true });
        $('.loadingOverlay').fadeOut();
        this.isAutoFillingShelves = false;
        this.closeAutoFillProductPicker();
      } catch (error) {
        console.error('[DisplayBuilder] Failed to auto fill shelves', error);
        selectedInstance.canvas.renderLock?.remove('display-builder:auto-fill');
        selectedInstance.canvas.requestRenderAll?.();
        $('.loadingOverlay').fadeOut();
        browser?.setError('Could not auto fill shelves');
        browser?.finishAutoFill();
        this.isAutoFillingShelves = false;
        window.displayBuilderHelper?.clearPendingShelfTarget?.();
      }
    },
    canMoveShelf(shelf, direction) {
      const shelves = this.currentShelves;
      const index = shelves.findIndex((item) => {
        return String(item.id) === String(shelf?.id);
      });

      if (index === -1) {
        return false;
      }

      const targetIndex = index + direction;

      return targetIndex >= 0 && targetIndex < shelves.length;
    },
    moveUp(shelf) {
      this.moveShelf(shelf, -1);
    },
    moveDown(shelf) {
      this.moveShelf(shelf, 1);
    },
    getShelfLayoutSnapshot(shelf, index = null) {
      const fixtureKey = this.getFixtureKeyForShelf(shelf);
      const fixtureShelves = this.sections[fixtureKey]?.shelves || [];
      const shelfIndex = index ?? fixtureShelves.findIndex((item) => {
        return String(item.id) === String(shelf?.id);
      });
      const fixtureRect = window.displayBuilderHelper?.rects?.[fixtureKey]
        || this.state.fixtures?.[fixtureKey]?.rect;
      const existingShelf = this.state.fixtures?.[fixtureKey]?.shelves?.find((item) => {
        return String(item.id) === String(shelf?.id);
      });
      const fittedShelf = fixtureRect
        ? fitShelvesToFixture(fixtureShelves, fixtureRect)[shelfIndex]
        : null;

      return {
        x: shelf.x ?? existingShelf?.x ?? fittedShelf?.x,
        y: shelf.y ?? existingShelf?.y ?? fittedShelf?.y,
        width: shelf.width ?? existingShelf?.width ?? fittedShelf?.width,
        height: shelf.height ?? existingShelf?.height ?? fittedShelf?.height,
      };
    },
    moveShelf(shelf, direction) {
      if (!this.isEditMode || !this.canMoveShelf(shelf, direction)) {
        return;
      }

      const shelves = this.currentShelves;
      const index = shelves.findIndex((item) => {
        return String(item.id) === String(shelf.id);
      });
      const targetIndex = index + direction;
      const targetShelf = shelves[targetIndex];
      const sourceLayout = this.getShelfLayoutSnapshot(shelf, index);
      const targetLayout = this.getShelfLayoutSnapshot(targetShelf, targetIndex);

      this.$set(shelf, 'x', targetLayout.x);
      this.$set(shelf, 'y', targetLayout.y);
      this.$set(shelf, 'width', targetLayout.width);
      this.$set(shelf, 'height', targetLayout.height);
      this.$set(targetShelf, 'x', sourceLayout.x);
      this.$set(targetShelf, 'y', sourceLayout.y);
      this.$set(targetShelf, 'width', sourceLayout.width);
      this.$set(targetShelf, 'height', sourceLayout.height);

      shelves.splice(index, 1);
      shelves.splice(targetIndex, 0, shelf);

      shelves.forEach((item, itemIndex) => {
        this.$set(item, 'number', itemIndex + 1);
      });

      this.selectShelf(shelf);
      this.syncCurrentLayoutToCanvas();

      this.$nextTick(() => {
        window.displayBuilderHelper?.positionProductsForShelf?.(
          this.getFixtureKeyForShelf(shelf),
          shelf.id
        );
        window.displayBuilderHelper?.positionProductsForShelf?.(
          this.getFixtureKeyForShelf(targetShelf),
          targetShelf.id
        );
        window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
        this.getDisplayBuilderCanvas()?.requestRenderAll?.();
      });
    },
    async removeShelf(shelfId) {
      const shelves = this.currentShelves;

      const idx = shelves.findIndex((shelf) => {
        return String(shelf.id) === String(shelfId);
      });

      if (idx === -1) {
        return;
      }

      const removedShelf = shelves[idx];

      await this.removeShelfProductsFromCanvas(removedShelf);

      shelves.splice(idx, 1);

      shelves.forEach((shelf, index) => {
        shelf.number = index + 1;
      });

      this.syncCurrentLayoutToCanvas();

      this.$emit('shelf-removed', {
        shelfId,
      });
    },

    alignShelf(shelf, alignment) {
      const fixtureKey = this.getFixtureKeyForShelf(shelf);
      const targetShelf = this.sections[fixtureKey]?.shelves?.find((item) => {
        return String(item.id) === String(shelf.id);
      });

      if (!targetShelf) {
        return;
      }

      this.$set(targetShelf, 'align', alignment);
      this.setActiveFixture(fixtureKey);

      window.displayBuilderHelper?.setActiveShelf?.(fixtureKey, targetShelf.id);
      this.markDisplayBuilderDirty();

      this.$nextTick(() => {
        window.displayBuilderHelper?.positionProductsForShelf?.(
          fixtureKey,
          targetShelf.id
        );
      });

      this.$emit('shelf-align', { shelfId: targetShelf.id, alignment });
    },
    setShelfSkew(shelf, skew) {
      const angle = Number(skew || 0);
      const fixtureKey = this.getFixtureKeyForShelf(shelf);

      this.$set(shelf, 'skew', angle);

      const helperShelf = window.displayBuilderHelper?.state?.fixtures?.[fixtureKey]?.shelves?.find((item) => {
        return String(item.id) === String(shelf.id);
      });

      if (helperShelf) {
        helperShelf.skew = angle;
      }

      window.displayBuilderHelper?.refreshShelfGeometry?.(fixtureKey, shelf.id);
      this.markDisplayBuilderDirty();
      this.$emit('shelf-skew-change', {
        fixture: fixtureKey,
        shelf,
        skew: angle,
      });
    },
    setShelfProductSpacing(shelf, spacing) {
      const normalizedSpacing = normalizeShelfProductSpacing(spacing);
      const fixtureKey = this.getFixtureKeyForShelf(shelf);

      this.$set(shelf, 'productSpacing', normalizedSpacing);

      const helperShelf = window.displayBuilderHelper?.state?.fixtures?.[fixtureKey]?.shelves?.find((item) => {
        return String(item.id) === String(shelf.id);
      });

      if (helperShelf) {
        helperShelf.productSpacing = normalizedSpacing;
      }

      window.displayBuilderHelper?.positionProductsForShelf?.(fixtureKey, shelf.id);
      window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
      this.markDisplayBuilderDirty();
      this.$emit('shelf-product-spacing-change', {
        fixture: fixtureKey,
        shelf,
        productSpacing: normalizedSpacing,
      });
    },
    setShelfDepth(shelf, depth) {
      const normalizedDepth = normalizeShelfDepth(depth);
      const fixtureKey = this.getFixtureKeyForShelf(shelf);

      this.$set(shelf, 'depth', normalizedDepth);

      const helperShelf = window.displayBuilderHelper?.state?.fixtures?.[fixtureKey]?.shelves?.find((item) => {
        return String(item.id) === String(shelf.id);
      });

      if (helperShelf) {
        helperShelf.depth = normalizeShelfDepth(normalizedDepth);
      }

      window.displayBuilderHelper?.positionProductsForShelf?.(fixtureKey, shelf.id);
      window.displayBuilderHelper?.scheduleShelfWarpRefresh?.(fixtureKey, shelf.id, { force: true });
      window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
      this.markDisplayBuilderDirty();
      this.$emit('shelf-depth-change', {
        fixture: fixtureKey,
        shelf,
        depth: normalizedDepth,
      });
    },
    applyShelfDepthToAll(depth) {
      const normalizedDepth = normalizeShelfDepth(depth);

      Object.entries(this.sections || {}).forEach(([fixtureKey, section]) => {
        (section.shelves || []).forEach((shelf) => {
          this.$set(shelf, 'depth', normalizeShelfDepth(normalizedDepth));

          const helperShelf = window.displayBuilderHelper?.state?.fixtures?.[fixtureKey]?.shelves?.find((item) => {
            return String(item.id) === String(shelf.id);
          });

          if (helperShelf) {
            helperShelf.depth = normalizeShelfDepth(normalizedDepth);
          }

          window.displayBuilderHelper?.positionProductsForShelf?.(fixtureKey, shelf.id);
          window.displayBuilderHelper?.scheduleShelfWarpRefresh?.(fixtureKey, shelf.id, { force: true });
        });
      });

      window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
      this.markDisplayBuilderDirty();
    },
    resetShelfDepthForAll() {
      this.applyShelfDepthToAll(normalizeShelfDepth());
    },

    onGlobalShelfAlignClick(event) {
      const button = event.target?.closest?.('.align-buttons .align-btn-center');

      if (!button || !window.isDisplayBuilder) {
        return;
      }

      event.preventDefault();
      this.recenterShelvesInFixture();
    },
    syncCanvasProductSpacingControls() {
      const spacing = normalizeShelfProductSpacing(
        this.state.productSpacing,
        DEFAULT_PRODUCT_SPACING
      );
      const progress = (spacing - MIN_SHELF_PRODUCT_SPACING)
        / Math.max(1, MAX_SHELF_PRODUCT_SPACING - MIN_SHELF_PRODUCT_SPACING) * 100;

      document.querySelectorAll('.display-builder-spacing-control').forEach((control) => {
        const slider = control.querySelector('.display-builder-spacing-slider');
        const value = control.querySelector('.display-builder-spacing-value');
        const reset = control.querySelector('.display-builder-spacing-reset');
        const trigger = control.querySelector('.display-builder-spacing-trigger');

        if (slider) {
          slider.value = spacing;
          slider.style.background = `linear-gradient(to right, #2eb89a 0%, #2eb89a ${progress}%, #edf1f5 ${progress}%, #edf1f5 100%)`;
        }

        if (value) {
          value.textContent = `${spacing.toFixed(0)}%`;
        }

        if (reset) {
          reset.disabled = Math.abs(spacing - DEFAULT_PRODUCT_SPACING) <= Number.EPSILON;
        }

        trigger?.classList.toggle(
          'has-override',
          Math.abs(spacing - DEFAULT_PRODUCT_SPACING) > Number.EPSILON
        );
      });
    },
    setCanvasProductSpacing(spacing) {
      const normalizedSpacing = normalizeShelfProductSpacing(
        spacing,
        DEFAULT_PRODUCT_SPACING
      );

      this.$set(this.state, 'productSpacing', normalizedSpacing);

      if (!window.displayBuilderState) {
        window.displayBuilderState = this.state;
      } else {
        window.displayBuilderState.productSpacing = normalizedSpacing;
      }

      if (window.displayBuilderHelper?.state) {
        window.displayBuilderHelper.state.productSpacing = normalizedSpacing;
      }

      this.getAllPlanogramShelves().forEach(({ fixtureKey, shelf }) => {
        window.displayBuilderHelper?.positionProductsForShelf?.(fixtureKey, shelf.id);
      });

      window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
      this.getDisplayBuilderCanvas()?.requestRenderAll?.();
      this.syncCanvasProductSpacingControls();
      this.markDisplayBuilderDirty();
      this.$emit('canvas-product-spacing-change', {
        productSpacing: normalizedSpacing,
      });
    },

    recenterShelvesInFixture(fixtureKey = null) {
      const targetFixtureKey = fixtureKey
        || this.activeFixture
        || window.displayBuilderState?.active_fixture
        || this.getFixtureKeys()[0]
        || 'center';
      const fixtureRect = window.displayBuilderHelper?.rects?.[targetFixtureKey]
        || this.state.fixtures?.[targetFixtureKey]?.rect;
      const shelves = this.sections[targetFixtureKey]?.shelves || [];

      if (!fixtureRect || !shelves.length) {
        return;
      }

      shelves.forEach((shelf) => {
        const width = Math.min(
          Math.max(1, Number(shelf.width || fixtureRect.width || 1)),
          Number(fixtureRect.width || 1)
        );
        const height = Math.min(
          Math.max(1, Number(shelf.height || 1)),
          Number(fixtureRect.height || 1)
        );
        const maxY = Math.max(0, Number(fixtureRect.height || 0) - height);

        this.$set(shelf, 'x', Math.max(0, (Number(fixtureRect.width || 0) - width) / 2));
        this.$set(shelf, 'y', Math.min(Math.max(0, Number(shelf.y || 0)), maxY));
        this.$set(shelf, 'width', width);
        this.$set(shelf, 'height', height);
      });

      this.setActiveFixture(targetFixtureKey);
      this.markDisplayBuilderDirty();

      this.$nextTick(() => {
        window.displayBuilderHelper?.updateState?.(this.buildDisplayBuilderStateFromPanel());
      });
    },

    alignAllShelves(alignment) {
      const fixtureKey = this.activeFixture
        || window.displayBuilderState?.active_fixture
        || this.getFixtureKeys()[0]
        || 'center';

      this.sections[fixtureKey]?.shelves?.forEach((shelf) => {
        this.$set(shelf, 'align', alignment);
      });

      this.markDisplayBuilderDirty();

      this.$nextTick(() => {
        this.sections[fixtureKey]?.shelves?.forEach((shelf) => {
          window.displayBuilderHelper?.positionProductsForShelf?.(
            fixtureKey,
            shelf.id
          );
        });
      });
    },

    onFixtureSelected(event) {
      const fixture = event.detail.fixture;
      this.setActiveFixture(fixture);

      if (this.isEditMode) {
        this.$nextTick(() => {
          this.$refs.editLayout?.scrollFixtureIntoView?.(fixture);
        });
      }
    },
    onShelfSelected(event) {
      const { fixture, shelfId } = event.detail || {};

      if (!fixture || !shelfId) {
        return;
      }

      this.activeShelfId = shelfId;
      this.activeShelfFixture = fixture;
      this.setActiveFixture(fixture);

      this.$nextTick(() => {
        if (this.isEditMode) {
          this.$refs.editLayout?.scrollShelfIntoView?.(fixture, shelfId);
          return;
        }

        this.scrollShelfIntoView(shelfId);
      });
    },
    onSelectionCleared() {
      this.activeShelfId = null;
      this.activeShelfFixture = null;
    },
    scrollShelfIntoView(shelfId) {
      const container = this.$el?.querySelector?.('.shelves-container');
      const element = Array.from(
        container?.querySelectorAll?.('[data-shelf-id]') || []
      ).find((candidate) => String(candidate.dataset.shelfId) === String(shelfId));

      if (!container || !element) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const viewportPadding = 8;
      let nextScrollTop = null;

      if (elementRect.top < containerRect.top + viewportPadding) {
        nextScrollTop = container.scrollTop
          + elementRect.top
          - containerRect.top
          - viewportPadding;
      } else if (elementRect.bottom > containerRect.bottom - viewportPadding) {
        nextScrollTop = container.scrollTop
          + elementRect.bottom
          - containerRect.bottom
          + viewportPadding;
      }

      if (nextScrollTop === null) {
        return;
      }

      const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
      const top = Math.min(maxScrollTop, Math.max(0, nextScrollTop));

      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ top, behavior: 'smooth' });
      } else {
        container.scrollTop = top;
      }
    },
    onFixtureMoved(event) {
      const { fixture, rect, state } = event.detail || {};

      if (!fixture || !rect || !this.state.fixtures?.[fixture]) {
        return;
      }

      const currentSection = this.sections[fixture] || { shelves: [] };
      const previousRect = this.state.fixtures[fixture]?.rect || rect;
      const shelves = Array.isArray(state?.fixtures?.[fixture]?.shelves)
        ? state.fixtures[fixture].shelves
        : transformShelvesWithFixture(
          currentSection.shelves || [],
          previousRect,
          rect
        );

      if (!this.sections[fixture]) {
        this.$set(this.sections, fixture, { shelves });
      } else {
        this.$set(this.sections[fixture], 'shelves', shelves);
      }
      this.$set(this.state.fixtures[fixture], 'rect', rect);
      this.$set(
        this.state.fixtures[fixture],
        'corners',
        state?.fixtures?.[fixture]?.corners || this.state.fixtures[fixture].corners
      );
      this.$set(
        this.state.fixtures[fixture],
        'angle',
        Number(state?.fixtures?.[fixture]?.angle || 0)
      );
      this.$set(this.state.fixtures[fixture], 'shelves', shelves);

      if (state) {
        window.displayBuilderState = state;
      }
    },
    onChange(action, value, file = null) {
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value
      })
    },
    syncShelvesToCanvas() {
      if (!window.displayBuilderHelper) {
        clearTimeout(this.syncShelvesRetryTimer);

        this.syncShelvesRetryTimer = setTimeout(() => {
          this.syncShelvesToCanvas();
        }, 300);

        return;
      }

      window.displayBuilderHelper.syncShelvesFromPanel(this.sections);
    },
    selectTab(fixtureKey) {
      this.setActiveFixture(fixtureKey);
      window.displayBuilderHelper?.setActiveFixture(fixtureKey);
      this.markDisplayBuilderDirty();
    },
    onShelfMoved(event) {
      const { fixture, shelfId, shelf, live = false } = event.detail || {};

      if (!fixture || !shelfId || !shelf) {
        return;
      }

      const vueShelf = this.sections[fixture]?.shelves.find((item) => {
        return String(item.id) === String(shelfId);
      });
      const stateShelf = this.state.fixtures?.[fixture]?.shelves?.find((item) => {
        return String(item.id) === String(shelfId);
      });
      const shelfTargets = [...new Set([vueShelf, stateShelf].filter(Boolean))];

      shelfTargets.forEach((targetShelf) => {
        this.$set(targetShelf, 'x', shelf.x);
        this.$set(targetShelf, 'y', shelf.y);
        this.$set(targetShelf, 'width', shelf.width);
        this.$set(targetShelf, 'height', shelf.height);
        this.$set(targetShelf, 'yOffset', Number(shelf.yOffset || 0));
        this.$set(targetShelf, 'skew', Number(shelf.skew || 0));
        this.$set(
          targetShelf,
          'productSpacing',
          normalizeShelfProductSpacing(shelf.productSpacing)
        );
        this.$set(targetShelf, 'depth', normalizeShelfDepth(shelf.depth));
      });

      if (!live) {
        this.markDisplayBuilderDirty();
      }
    },
    buildDisplayBuilderStateFromPanel() {
      const currentState = window.displayBuilderState || {};
      const existingFixtures = currentState.fixtures || {};
      const fixtures = {};
      const fixtureKeys = this.getFixtureKeys();

      fixtureKeys.forEach((fixtureKey) => {
        const fixtureRect = window.displayBuilderHelper?.rects?.[fixtureKey];
        const existingFixture = existingFixtures[fixtureKey] || {};

        if (!Object.keys(existingFixture).length) {
          fixtures[fixtureKey] = {
            label: fixtureKey,
            rect: fixtureRect,
            shelves: [],
          };
        } else {
          fixtures[fixtureKey] = {
            ...existingFixture,
          };
        }

        fixtures[fixtureKey].rect = fixtures[fixtureKey].rect || fixtureRect;
        fixtures[fixtureKey].label = this.tabs.find((tab) => tab.key === fixtureKey)?.label || fixtures[fixtureKey].label || fixtureKey;
        fixtures[fixtureKey].shelfCount = this.sections[fixtureKey]?.shelves?.length || fixtures[fixtureKey].shelfCount || 0;

        const existingShelves = fixtures[fixtureKey].shelves || [];

        fixtures[fixtureKey].shelves = (this.sections[fixtureKey]?.shelves || []).map((shelf, index) => {
          const existing = existingShelves.find((item) => {
            return String(item.id) === String(shelf.id);
          });

          return {
            id: shelf.id,
            number: shelf.number,

            x: shelf.x ?? existing?.x ?? 0,
            y: shelf.y ?? existing?.y ?? (110 + index * 115),

            width: shelf.width ?? existing?.width ?? fixtureRect?.width ?? 260,
            height: shelf.height || existing?.height || 85,
            yOffset: Number(shelf.yOffset ?? existing?.yOffset ?? 0),
            skew: Number(shelf.skew ?? existing?.skew ?? 0),
            productSpacing: normalizeShelfProductSpacing(shelf.productSpacing),
            depth: normalizeShelfDepth(shelf.depth ?? existing?.depth),

            align: shelf.align || existing?.align || 'center',

            products: shelf.products.map((product) => ({
              id: product.id,

              canvas_object_id: product.canvas_object_id || null,
              source_canvas_object_id: product.source_canvas_object_id || null,
              facing_object_ids: product.facing_object_ids || [],

              facings: product.facings || 1,
              defaultFacings: product.defaultFacings || product.facings || 1,

              name: product.name || '',
              sku: product.sku || '',

              fileName: product.fileName || '',
              thumbnail: product.thumbnail ? String(product.thumbnail) : null,
              image: product.image || product.thumbnail || null,
              objectId: product.objectId || null,
              templateFieldId: product.templateFieldId || null,
              tilt: Number(product.tilt || 0),
            }))
          };
        });
      });

      const activeFixture = [this.activeFixture, currentState.active_fixture]
        .find((fixtureKey) => fixtureKeys.includes(fixtureKey)) || fixtureKeys[0] || null;

      return {
        ...currentState,
        active_fixture: activeFixture,
        fixture_order: fixtureKeys,
        fixtures,
        logos: (this.state.logos || []).map((logo) => ({
                canvas_object_id: logo.canvas_object_id,
                assetKey: logo.assetKey || null,
                fileName: logo.fileName || '',
                thumbnail: logo.thumbnail || null,
              })),
      };
    },
    onProductAssignedToShelf(event) {
      const { fixture, shelfId, canvasObjectId, object } = event.detail;

      const shelf = this.sections[fixture]?.shelves.find((item) => {
        return String(item.id) === String(shelfId);
      });

      if (!shelf) {
        this.dispatchProductPositionedOnShelf(event.detail, { skipped: true });
        return;
      }

      const pendingProduct = [...shelf.products].reverse().find((item) => {
        return !item.canvas_object_id;
      });
      const existingProduct = this.findShelfProductForAdditionalObject(shelf, object, canvasObjectId);
      const productWithCanvasObjectId = this.findShelfProductByCanvasObjectId(shelf, canvasObjectId);

      if (productWithCanvasObjectId && productWithCanvasObjectId !== pendingProduct) {
        if (pendingProduct) {
          const pendingIndex = shelf.products.indexOf(pendingProduct);

          if (pendingIndex !== -1) {
            shelf.products.splice(pendingIndex, 1);
          }
        }

        this.$nextTick(() => {
          this.positionShelfProducts(shelf);
          this.dispatchProductPositionedOnShelf(event.detail);
        });
        return;
      }

      if (existingProduct) {
        this.addProductObjectAsFacing(existingProduct, object, canvasObjectId);

        if (pendingProduct) {
          const pendingIndex = shelf.products.indexOf(pendingProduct);

          if (pendingIndex !== -1) {
            shelf.products.splice(pendingIndex, 1);
          }
        }

        this.markDisplayBuilderDirty();
        this.$nextTick(() => {
          this.positionShelfProducts(shelf);
          this.dispatchProductPositionedOnShelf(event.detail);
        });
        return;
      }

      let product = pendingProduct;

      if (!product) {
        product = this.makeProductFromCanvasObject(object, canvasObjectId);
        shelf.products.push(product);

        this.markDisplayBuilderDirty();
        this.$nextTick(() => {
          this.positionShelfProducts(shelf);
          this.dispatchProductPositionedOnShelf(event.detail);
        });
        return;
      }

      const thumbnail = this.getThumbnailForProductObject(object);
      const productName = this.getShelfProductNameForObject(object, product.name);

      product.canvas_object_id = canvasObjectId;
      product.source_canvas_object_id = object.displayBuilderSourceObjectId || canvasObjectId;
      product.name = productName || object.name;
      product.product_name = productName || product.product_name || product.name;
      product.fileName = object.fileName;
      product.thumbnail = thumbnail;
      product.image = thumbnail;
      product.objectId = object.id;
      product.templateFieldId = object.templateFieldId || object.fieldId;
      product.url = object._element?.currentSrc || object._element?.src || null;

      if (product.tilt === undefined || product.tilt === null) {
        this.$set(product, 'tilt', this.getCanvasObjectProductTilt(object));
      }

      if (!Array.isArray(product.facing_object_ids)) {
        this.$set(product, 'facing_object_ids', []);
      }

      product.facings = 1 + product.facing_object_ids.length;
      this.applyTiltToProductObjects(product, product.tilt);

      this.markDisplayBuilderDirty();
      this.$nextTick(() => {
        this.positionShelfProducts(shelf);
        this.dispatchProductPositionedOnShelf(event.detail);
      });
    },
    dispatchProductPositionedOnShelf(detail = {}, extra = {}) {
      window.dispatchEvent(new CustomEvent('display-builder:product-positioned-on-shelf', {
        detail: {
          ...detail,
          ...extra,
        },
      }));
    },
    getThumbnailForProductObject(object) {
      if (!object?._element?.currentSrc && !object?._element?.src) {
        return null;
      }

      try {
        const url = new URL(object._element.currentSrc || object._element.src, window.location.origin);
        if (url.searchParams.size > 0) {
          url.searchParams.set('lightweight', '1');

          if (String(object.id || '').startsWith(IMAGE_PREFIX_ID)) {
            url.searchParams.set('scale_width', '40');
          } else {
            url.searchParams.set('is_thumbnail', '1');
          }
        }

        return url;
      } catch (error) {
        return object._element.currentSrc || object._element.src || null;
      }
    },
    makeProductFromCanvasObject(object, canvasObjectId) {
      const thumbnail = this.getThumbnailForProductObject(object);
      const productName = this.getShelfProductNameForObject(
        object,
        object?.name || object?.fileName || 'Product'
      );

      return {
        id: `product-${canvasObjectId || Date.now()}-${Math.random().toString(36).slice(2)}`,
        canvas_object_id: canvasObjectId,
        source_canvas_object_id: object?.displayBuilderSourceObjectId || canvasObjectId,
        facing_object_ids: [],
        facings: 1,
        defaultFacings: 1,
        name: productName,
        product_name: productName,
        sku: '',
        fileName: object?.fileName || '',
        thumbnail,
        image: thumbnail,
        objectId: object?.id || canvasObjectId,
        templateFieldId: object?.templateFieldId || object?.fieldId || null,
        tilt: this.getCanvasObjectProductTilt(object),
        url: object._element?.currentSrc || object._element?.src || null
      };
    },
    getCanvasObjectProductTilt(object) {
      const tilt = Number(object?.displayBuilderProductTilt);

      return Number.isFinite(tilt) ? tilt : 0;
    },
    getShelfProductKey(product, index = 0) {
      if (product?.id) {
        return product.id;
      }

      return [
        product?.canvas_object_id,
        product?.objectId,
        product?.fileName,
        index,
      ].filter((value) => value !== undefined && value !== null && value !== '').join(':') || `product-${index}`;
    },
    findShelfProductByCanvasObjectId(shelf, canvasObjectId) {
      if (!shelf || !canvasObjectId) {
        return null;
      }

      return (shelf.products || []).find((product) => {
        return String(product?.canvas_object_id || '') === String(canvasObjectId);
      }) || null;
    },
    findShelfProductForAdditionalObject(shelf, object, canvasObjectId) {
      const objectFileName = this.normalizeProductFileName(object?.fileName);

      if (!objectFileName) {
        return null;
      }

      return (shelf.products || []).find((product) => {
        if (!product?.canvas_object_id || String(product.canvas_object_id) === String(canvasObjectId)) {
          return false;
        }

        if (Array.isArray(product.facing_object_ids)
          && product.facing_object_ids.some((id) => String(id) === String(canvasObjectId))) {
          return false;
        }

        return this.isSameProductFileName(product.fileName, objectFileName);
      }) || null;
    },
    addProductObjectAsFacing(product, object, canvasObjectId) {
      if (!product || !canvasObjectId) {
        return false;
      }

      if (!Array.isArray(product.facing_object_ids)) {
        this.$set(product, 'facing_object_ids', []);
      }

      if (String(product.canvas_object_id) === String(canvasObjectId)
        || product.facing_object_ids.some((id) => String(id) === String(canvasObjectId))) {
        this.updateProductFacingCount(product);
        return false;
      }

      product.facing_object_ids.push(canvasObjectId);
      this.updateProductFacingCount(product);

      if (object && (product.tilt === undefined || product.tilt === null)) {
        this.$set(product, 'tilt', this.getCanvasObjectProductTilt(object));
      }

      this.applyTiltToProductObjects(product, product.tilt || 0);
      return true;
    },
    markDisplayBuilderDirty: _.debounce(function(props = {}) {
      const { onShelfLoaded = false } = props;
      
      if (onShelfLoaded) {
        window.displayBuilderHelper.shelfLoadLock = null;
        window.displayBuilderHelper.canvas.renderLock.remove('display-builder:shelf-load');
      }
      
      const state = this.buildDisplayBuilderStateFromPanel();
      window.displayBuilderState = state;
      window.displayBuilderHelper?.updateState(state);

      const { updateLayoutChanges } = getShowPageScope();
      const selectedInstance = this.getSelectedInstance();
      const instanceId = selectedInstance?.id || window.previews?.[0]?.id;
      if (updateLayoutChanges && instanceId) {
        updateLayoutChanges(instanceId, 'display_builder', state);
      }
      window.dispatchEvent(new CustomEvent('display-builder:changed', { detail: { state } }));
    }, 150),
    parseDisplayBuilderState() {
      if (!this.displayBuilderState) {
        return {
          active_fixture: 'center',
          fixtures: {},
        };
      }

      if (typeof this.displayBuilderState === 'object') {
        return this.displayBuilderState;
      }

      try {
        return JSON.parse(this.displayBuilderState);
      } catch (error) {
        console.warn('[DisplayBuilder] Invalid displayBuilderStateJson', this.displayBuilderState, error);

        return {
          active_fixture: 'center',
          fixtures: {},
        };
      }
    },
    onCanvasProductDeleted(event) {
      const canvasObjectId = event.detail?.canvasObjectId;

      if (!canvasObjectId) {
        return;
      }

      let changed = false;
      const facingObjectIdsToRemove = new Set();

      this.getFixtureKeys().forEach((fixtureKey) => {
        (this.sections[fixtureKey]?.shelves || []).forEach((shelf) => {
          const before = shelf.products.length;

          shelf.products.forEach((product) => {
            if (!Array.isArray(product.facing_object_ids)) {
              return;
            }

            if (String(product.canvas_object_id) === String(canvasObjectId)) {
              product.facing_object_ids.forEach((id) => {
                if (id) {
                  facingObjectIdsToRemove.add(String(id));
                }
              });

              return;
            }

            const facingIndex = product.facing_object_ids.findIndex((id) => {
              return String(id) === String(canvasObjectId);
            });

            if (facingIndex === -1) {
              return;
            }

            product.facing_object_ids.splice(facingIndex, 1);
            product.facings = 1 + product.facing_object_ids.length;
            changed = true;
          });

          shelf.products = shelf.products.filter((product) => {
            return String(product.canvas_object_id) !== String(canvasObjectId);
          });

          if (shelf.products.length !== before) {
            changed = true;
          }
        });
      });

      if (!changed) {
        return;
      }

      this.removeFacingObjectsFromCanvas([...facingObjectIdsToRemove]);
      this.markDisplayBuilderDirty();

      this.$nextTick(() => {
        setTimeout(() => {
          window.displayBuilderHelper?.restoreProductAssignmentsFromState?.();
        }, 300);
      });
    },
    removeFacingObjectsFromCanvas(canvasObjectIds = []) {
      const canvas = this.getDisplayBuilderCanvas();

      if (!canvas || !canvasObjectIds.length) {
        return;
      }

      canvasObjectIds.forEach((canvasObjectId) => {
        const target = window.displayBuilderHelper?.getProductObjectByStableId?.(canvasObjectId)
          || canvas.getObjects().find((object) => {
            return String(object.id) === String(canvasObjectId);
          });

        if (target) {
          canvas.remove(target);
        }
      });

      canvas.discardActiveObject();
      canvas.requestRenderAll();
    },
    async duplicateProductForShelf(shelf, product) {
      this.refreshSelectedInstance();
      this.reconcileShelfProductsWithCanvas();

      const selectedInstance = this.getSelectedInstance();
      const canvas = selectedInstance?.canvas || this.getDisplayBuilderCanvas();
      const instanceId = selectedInstance?.id || canvas?.instance_id || window.displayBuilderInstanceId;

      if (!canvas || !instanceId || !product) {
        console.warn('[DisplayBuilder] Cannot duplicate product without an active canvas', {
          instanceId,
          product,
        });
        return;
      }

      const selected = canvas.getObjects().find(
        obj => String(obj.id) === String(product.canvas_object_id)
      );

      if (!selected || (selected.fieldType !== FIELD_TYPE_PRODUCT_IMAGE && !selected.isDisplayBuilderFacing)) {
        console.warn('[DisplayBuilder] Product image object not found for duplicate', {
          product,
          selected,
        });

        return;
      }

      try {
        const fixtureKey = this.getFixtureKeyForShelf(shelf);
        const shelfId = shelf.id;
        const canvasObjectId = this.createFacingObjectId(product);
        const newObject = await this.cloneProductObjectForFacing(
          selected,
          canvasObjectId
        );

        canvas.add(newObject);

        if (!Array.isArray(product.facing_object_ids)) {
          this.$set(product, 'facing_object_ids', []);
        }

        if (!product.facing_object_ids.includes(canvasObjectId)) {
          product.facing_object_ids.push(canvasObjectId);
        }

        this.$set(product, 'facings', 1 + product.facing_object_ids.length);
        // this.applyTiltToProductObjects(product, product.tilt || 0);

        this.markDisplayBuilderDirty();
        this.markDisplayBuilderDirty.flush?.();

        this.positionShelfProducts(shelf);
        window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
        canvas.requestRenderAll();
      } catch (error) {
        console.error('[DisplayBuilder] Failed duplicating product image', error);
      }
    },
    createFacingObjectId(product) {
      const sourceObjectId = product.canvas_object_id || product.objectId || product.id || 'product';
      const safeSourceObjectId = String(sourceObjectId).replace(/[^A-Za-z0-9_-]/g, '-');
      const nextFacingIndex = Array.isArray(product.facing_object_ids)
        ? product.facing_object_ids.length + 1
        : Math.max(1, product.facings || 1);

      return [
        'display-builder-facing',
        safeSourceObjectId,
        nextFacingIndex,
        Date.now(),
      ].join('-');
    },
    getProductCanvasObjectIds(product) {
      const objectIds = [];

      if (product?.canvas_object_id) {
        objectIds.push(product.canvas_object_id);
      }

      if (product?.objectId) {
        objectIds.push(product.objectId);
      }

      if (Array.isArray(product?.facing_object_ids)) {
        objectIds.push(...product.facing_object_ids);
      }

      return [...new Set(objectIds.filter(Boolean).map(String))];
    },
    getProductFacingCount(product) {
      return Math.max(1, this.getProductCanvasObjectIds(product).length || Number(product?.facings || 1));
    },
    updateProductFacingCount(product) {
      if (!product) {
        return 1;
      }

      const facingCount = this.getProductFacingCount(product);
      this.$set(product, 'facings', facingCount);

      return facingCount;
    },
    findUntrackedAdditionalProductObjectId(shelf, product) {
      const fixtureKey = this.getFixtureKeyForShelf(shelf);
      const productObjects = window.displayBuilderHelper?.findProductObjectsForShelf?.(fixtureKey, shelf.id) || [];
      const trackedIds = new Set(this.getProductCanvasObjectIds(product));
      const productFileName = this.normalizeProductFileName(product?.fileName);

      const match = [...productObjects].reverse().find((object) => {
        return object?.id
          && !trackedIds.has(String(object.id))
          && this.isSameProductFileName(object.fileName, productFileName);
      });

      return match?.id || null;
    },
    getProductCanvasObjects(product) {
      const canvas = this.getDisplayBuilderCanvas();

      if (!canvas || !product) {
        return [];
      }

      const objectIds = this.getProductCanvasObjectIds(product);

      return objectIds
        .map((objectId) => canvas.getObjects().find((object) => {
          return String(object.id) === String(objectId);
        }))
        .filter(Boolean);
    },
    applyTiltToProductObjects(product, tilt, render = true) {
      const canvas = this.getDisplayBuilderCanvas();
      const angle = Number(tilt || 0);

      if (!canvas || !product) {
        return;
      }

      this.getProductCanvasObjects(product).forEach((object) => {
        object.displayBuilderProductTilt = angle;
        object.set({
          angle,
          objectCaching: false,
        });

        object.setCoords();

        canvas.fire('object:modified', {
          target: object,
        });
      });

      if (render) {
        canvas.requestRenderAll();
      }
    },
    setProductTilt(shelf, product, tilt) {
      const angle = Number(tilt || 0);

      this.$set(product, 'tilt', angle);
      this.applyTiltToProductObjects(product, angle);
      this.positionShelfProducts(shelf);
      this.markDisplayBuilderDirty();

      this.$emit('tilt-change', { shelf, product, tilt: angle });
    },
    applyProductTiltToAll(tilt) {
      const angle = Number(tilt || 0);

      Object.entries(this.sections || {}).forEach(([fixtureKey, section]) => {
        (section.shelves || []).forEach((shelf) => {
          (shelf.products || []).forEach((product) => {
            this.$set(product, 'tilt', angle);
            this.applyTiltToProductObjects(product, angle, false);
          });

          window.displayBuilderHelper?.positionProductsForShelf?.(
            fixtureKey,
            shelf.id
          );
        });
      });

      window.displayBuilderHelper?.orderDisplayBuilderLayers?.();
      this.getDisplayBuilderCanvas()?.requestRenderAll?.();
      this.markDisplayBuilderDirty();

      this.$emit('tilt-change', { tilt: angle, applyToAll: true });
    },
    cloneProductObjectForFacing(sourceObject, canvasObjectId) {
      return new Promise((resolve, reject) => {
        try {
          const cloned = cloneFabricObject(sourceObject);
          const productName = this.getShelfProductNameForObject(
            sourceObject,
            sourceObject?.fileName || 'Product'
          );

          cloned.set({
            id: canvasObjectId,
            name: productName,
            left: sourceObject.left,
            top: sourceObject.top,
            scaleX: sourceObject.scaleX,
            scaleY: sourceObject.scaleY,
            angle: sourceObject.angle || 0,
            flipX: !!sourceObject.flipX,
            flipY: !!sourceObject.flipY,
            opacity: sourceObject.opacity,
            visible: true,
            selectable: true,
            evented: true,
            lockMovementX: true,
            lockMovementY: true,
            lockScalingX: true,
            lockScalingY: true,
            lockRotation: true,
            hasControls: false,
            hasBorders: true,
            objectCaching: false,
          });

          cloned.fileName = sourceObject.fileName;
          cloned.product_name = sourceObject.product_name;
          cloned.title = sourceObject.title;
          cloned.displayBuilderSourceObjectId = sourceObject.id;
          cloned.isDisplayBuilderFacing = true;

          // Clear template-specific props that shouldn't carry over
          ['templateField','fieldId','fieldType','element_id',
          'updateLayoutChanges','deleteData','deleteLayer'].forEach(k => {
            delete cloned[k];
          });

          resolve(cloned);
        } catch (error) {
          reject(error);
        }
      });
    },
    removeProductImageDataForObject(target) {
      const index = parseInt(String(target?.id || '').replace(IMAGE_PREFIX_ID, ''), 10);

      if (!Number.isFinite(index)) {
        return;
      }

      const {
        previews,
        getChangesLayoutById,
        updateLayoutChanges,
      } = getShowPageScope();
      const instanceId = target.canvas?.instance_id;
      const preview = previews?.find((item) => String(item.id) === String(instanceId));

      if (!preview || !updateLayoutChanges) {
        return;
      }

      const data = {
        ...(preview.data || {}),
        ...(getChangesLayoutById ? getChangesLayoutById(instanceId) : {}),
      };
      const keys = [
        'file_ids',
        'angle',
        'x_offset',
        'y_offset',
        'scale',
        'p_shadow',
        'p_opacity',
        'p_visible',
        'p_oval_shadows',
        'p_mirror_shadows',
        'p_flipX',
        'p_flipY',
        'p_width',
        'p_height',
        'moveable',
      ];

      keys.forEach((key) => {
        if (!data[key]) {
          return;
        }

        if (key === 'file_ids') {
          const nextFileIds = String(data[key])
            .split(/[, ]+/)
            .filter(Boolean)
            .filter((_, itemIndex) => itemIndex !== index)
            .join(' ');

          updateLayoutChanges(instanceId, key, nextFileIds);
          return;
        }

        if (!Array.isArray(data[key])) {
          return;
        }

        updateLayoutChanges(
          instanceId,
          key,
          data[key].filter((_, itemIndex) => itemIndex !== index)
        );
      });

      return index;
    },
    compactProductImageObjectIdsAfterRemoval(removedIndex) {
      const canvas = this.getDisplayBuilderCanvas();
      const getNextId = (id) => {
        const index = parseInt(String(id || '').replace(IMAGE_PREFIX_ID, ''), 10);

        if (!Number.isFinite(index) || index <= removedIndex) {
          return id;
        }

        return `${IMAGE_PREFIX_ID}${index - 1}`;
      };

      if (canvas) {
        canvas.getObjects()
          .filter((object) => String(object?.id || '').startsWith(IMAGE_PREFIX_ID))
          .sort((a, b) => {
            const aIndex = parseInt(String(a.id || '').replace(IMAGE_PREFIX_ID, ''), 10);
            const bIndex = parseInt(String(b.id || '').replace(IMAGE_PREFIX_ID, ''), 10);

            return aIndex - bIndex;
          })
          .forEach((object) => {
            const currentId = String(object?.id || '');

            const nextId = getNextId(currentId);

            if (nextId === currentId) {
              return;
            }

            object.set({
              id: nextId,
              name: nextId,
              element_id: nextId,
            });

            object.id = nextId;
            object.name = nextId;
            object.element_id = nextId;

            if (object.templateField) {
              object.templateField.element_id = nextId;
            }
          });
      }

      this.getFixtureKeys().forEach((fixtureKey) => {
        (this.sections[fixtureKey]?.shelves || []).forEach((shelf) => {
          (shelf.products || []).forEach((product) => {
            if (product.canvas_object_id) {
              this.$set(product, 'canvas_object_id', getNextId(product.canvas_object_id));
            }

            if (product.objectId) {
              this.$set(product, 'objectId', getNextId(product.objectId));
            }

            if (Array.isArray(product.facing_object_ids)) {
              this.$set(
                product,
                'facing_object_ids',
                product.facing_object_ids.map((id) => getNextId(id))
              );
            }

            this.updateProductFacingCount(product);
          });
        });
      });
    },
    async removeFacingObjectFromCanvas(canvasObjectId) {
      const canvas = this.getDisplayBuilderCanvas();

      if (!canvas || !canvasObjectId) {
        return false;
      }

      const target = window.displayBuilderHelper?.getProductObjectByStableId?.(canvasObjectId)
        || canvas.getObjects().find((object) => {
          return String(object.id) === String(canvasObjectId);
        });

      if (!target) {
        return false;
      }

      let removedProductImageIndex = null;

      if (String(target.id || '').startsWith(IMAGE_PREFIX_ID)) {
        removedProductImageIndex = this.removeProductImageDataForObject(target);
      }

      canvas.remove(target);

      if (Number.isFinite(removedProductImageIndex)) {
        this.compactProductImageObjectIdsAfterRemoval(removedProductImageIndex);
      }

      canvas.discardActiveObject();
      canvas.requestRenderAll();

      return true;
    },
    getFixtureKeyForShelf(shelf) {
      const fixtureByReference = this.getFixtureKeys().find((fixtureKey) => {
        return this.sections[fixtureKey]?.shelves?.some((item) => item === shelf);
      });

      if (fixtureByReference) {
        return fixtureByReference;
      }

      return this.getFixtureKeys().find((fixtureKey) => {
        return this.sections[fixtureKey]?.shelves?.some((item) => {
          return String(item.id) === String(shelf.id);
        });
      }) || this.activeFixture || this.getFixtureKeys()[0] || 'center';
    },
    normalizeProductFileName(fileName) {
      return String(fileName || '').trim().replace(/\.[^/.]+$/, '');
    },
    isSameProductFileName(a, b) {
      return this.normalizeProductFileName(a) === this.normalizeProductFileName(b);
    },
    setProductCanvasObject(product, object) {
      if (!product || !object?.id) {
        return;
      }

      const previousObjectId = String(product.canvas_object_id || '');
      const nextObjectId = String(object.id);
      const previousFacingObjectIds = Array.isArray(product.facing_object_ids)
        ? [...product.facing_object_ids]
        : [];

      this.$set(product, 'canvas_object_id', object.id);
      this.$set(product, 'objectId', object.id);
      this.$set(product, 'source_canvas_object_id', object.displayBuilderSourceObjectId || object.id);

      if (object.templateFieldId || object.fieldId) {
        this.$set(product, 'templateFieldId', object.templateFieldId || object.fieldId);
      }

      if (object.fileName) {
        this.$set(product, 'fileName', object.fileName);
      }

      const productName = this.getShelfProductNameForObject(object, product.name);

      if (productName) {
        this.$set(product, 'name', productName);
        this.$set(product, 'product_name', productName);
      }

      if (
        previousObjectId &&
        previousObjectId !== nextObjectId &&
        Array.isArray(product.facing_object_ids) &&
        product.facing_object_ids.length
      ) {
        const canvas = this.getDisplayBuilderCanvas();
        const safeNextObjectId = nextObjectId.replace(/[^A-Za-z0-9_-]/g, '-');
        const timestamp = Date.now();

        previousFacingObjectIds.forEach((facingObjectId) => {
          const facingObject = canvas?.getObjects?.().find((canvasObject) => {
            return String(canvasObject.id) === String(facingObjectId);
          });

          if (facingObject) {
            canvas.remove(facingObject);
          }
        });

        this.$set(product, 'facing_object_ids', product.facing_object_ids.map((_, index) => {
          return [
            'display-builder-facing',
            safeNextObjectId,
            index + 1,
            timestamp,
            index,
          ].join('-');
        }));
      }
    },
    reconcileShelfProductsWithCanvas() {
      const canvasObjects = this.getAssignableProductObjects();

      if (!canvasObjects.length) {
        return false;
      }

      const usedCanvasObjectIds = new Set();
      let changed = false;

      this.getShelfProductEntries().forEach(({ product }) => {
        const currentObjectId = String(product.canvas_object_id || '');
        const expectedFileName = this.normalizeProductFileName(product.fileName);
        const currentObject = canvasObjects.find((object) => {
          return String(object.id) === currentObjectId;
        });

        if (
          currentObject &&
          (!expectedFileName || this.isSameProductFileName(currentObject.fileName, expectedFileName))
        ) {
          const productName = this.getShelfProductNameForObject(
            currentObject,
            product.fileName || 'Product'
          );

          if (productName && productName !== product.name) {
            this.$set(product, 'name', productName);
            this.$set(product, 'product_name', productName);
            changed = true;
          }

          usedCanvasObjectIds.add(String(currentObject.id));
          return;
        }

        const replacement = canvasObjects.find((object) => {
          return !usedCanvasObjectIds.has(String(object.id))
            && expectedFileName
            && this.isSameProductFileName(object.fileName, expectedFileName);
        });

        if (!replacement) {
          return;
        }

        this.setProductCanvasObject(product, replacement);
        usedCanvasObjectIds.add(String(replacement.id));
        changed = true;
      });

      return changed;
    },
    restoreProductsOnCurrentCanvas() {
      if (!window.displayBuilderHelper) {
        return;
      }

      this.reconcileShelfProductsWithCanvas();

      const state = this.buildDisplayBuilderStateFromPanel();
      this.state = state;
      window.displayBuilderState = state;
      window.displayBuilderHelper.updateState(state);
      window.displayBuilderHelper.restoreProductAssignmentsFromState?.();
      window.displayBuilderHelper.orderDisplayBuilderLayers?.();
      this.getDisplayBuilderCanvas()?.requestRenderAll?.();
    },
    getShelfProductEntries() {
      const entries = [];

      this.getFixtureKeys().forEach((fixtureKey) => {
        (this.sections[fixtureKey]?.shelves || []).forEach((shelf) => {
          (shelf.products || []).forEach((product) => {
            if (!product?.canvas_object_id) {
              return;
            }

            entries.push({
              fixtureKey,
              shelf,
              product,
            });
          });
        });
      });

      return entries;
    },
    getAssignableProductObjects() {
      const canvas = this.getDisplayBuilderCanvas();

      if (!canvas) {
        return [];
      }

      if (window.displayBuilderHelper?.getAssignableProductObjects) {
        return window.displayBuilderHelper.getAssignableProductObjects();
      }

      return canvas.getObjects().filter((object) => {
        const id = String(object?.id || '');

        return id
          && !object.isDisplayBuilderFacing
          && !object.displayBuilderSourceObjectId
          && (
            object.displayBuilderType === 'shelf-product-image'
            || object.fieldType === FIELD_TYPE_PRODUCT_IMAGE
            || id.startsWith(IMAGE_PREFIX_ID)
          );
      });
    },
    reconcilePendingProductObjects(pendingTarget = {}) {
      const beforeObjects = Array.isArray(pendingTarget.beforeProductObjects)
        ? pendingTarget.beforeProductObjects
        : [];
      const selectedProductFileIds = [
        ...(Array.isArray(pendingTarget.selectedProductFileIds)
          ? pendingTarget.selectedProductFileIds
          : []),
        pendingTarget.selectedUploadFileId,
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean);

      if (!beforeObjects.length && !selectedProductFileIds.length) {
        return null;
      }

      const afterObjects = this.getAssignableProductObjects();
      const beforeById = new Map(beforeObjects.map((object) => {
        return [String(object.id), object];
      }));
      const afterById = new Map(afterObjects.map((object) => {
        return [String(object.id), object];
      }));
      const usedAfterIds = new Set();
      const claimedProducts = new Set();
      const productEntries = this.getShelfProductEntries();
      let changed = false;
      let shouldRestoreAssignments = false;

      productEntries.forEach(({ fixtureKey, shelf, product }) => {
        const objectId = String(product.canvas_object_id || '');
        const currentObject = afterById.get(objectId);
        const expectedFileName = product.fileName || beforeById.get(objectId)?.fileName || '';

        if (
          currentObject &&
          (!expectedFileName || this.isSameProductFileName(currentObject.fileName, expectedFileName))
        ) {
          if (
            currentObject.displayBuilderType !== 'shelf-product-image'
            || String(currentObject.fixtureKey || '') !== String(fixtureKey)
            || String(currentObject.shelfId || '') !== String(shelf.id)
          ) {
            shouldRestoreAssignments = true;
          }

          usedAfterIds.add(String(currentObject.id));
          claimedProducts.add(product);
        }
      });

      productEntries.forEach(({ product }) => {
        if (claimedProducts.has(product)) {
          return;
        }

        const objectId = String(product.canvas_object_id || '');
        const expectedFileName = product.fileName || beforeById.get(objectId)?.fileName || '';

        if (!expectedFileName) {
          return;
        }

        const replacement = afterObjects.find((object) => {
          return !usedAfterIds.has(String(object.id))
            && this.isSameProductFileName(object.fileName, expectedFileName);
        });

        if (!replacement) {
          return;
        }

        this.setProductCanvasObject(product, replacement);
        usedAfterIds.add(String(replacement.id));
        claimedProducts.add(product);
        changed = true;
      });

      if (changed) {
        const state = this.buildDisplayBuilderStateFromPanel();

        this.state = state;
        window.displayBuilderState = state;
        window.displayBuilderHelper?.updateState(state);
      }

      if (
        (changed || shouldRestoreAssignments)
        && pendingTarget.fixtureKey
        && pendingTarget.shelfId
      ) {
        window.displayBuilderHelper?.restoreProductAssignmentsFromState?.();
      }

      const existingProductObjectIds = new Set(
        productEntries
          .map(({ product }) => String(product.canvas_object_id || ''))
          .filter(Boolean)
      );

      const newObjects = afterObjects.filter((object) => {
        const objectId = String(object.id);
        const beforeObject = beforeById.get(objectId);

        if (usedAfterIds.has(objectId)) {
          return false;
        }

        if (existingProductObjectIds.has(objectId)) {
          return false;
        }

        return !beforeObject
          || !this.isSameProductFileName(object.fileName, beforeObject.fileName);
      });

      if (!selectedProductFileIds.length) {
        return newObjects;
      }

      const selectedProductObject = newObjects.find((object) => {
        return selectedProductFileIds.some((fileId) => {
          return this.isSameProductFileName(object.fileName, fileId);
        });
      });

      if (!selectedProductObject) {
        this.removeRejectedProductObjects(newObjects);
        return [];
      }

      const selectedProductObjectId = String(selectedProductObject.id);
      const unclaimedObjects = afterObjects.filter((object) => {
        const objectId = String(object.id);
        const isClaimed = usedAfterIds.has(objectId)
          || existingProductObjectIds.has(objectId)
          || objectId === selectedProductObjectId;

        return !isClaimed;
      });

      this.removeRejectedProductObjects(unclaimedObjects);

      return [selectedProductObject];
    },
    onDisplayBuilderLogoAdded(event) {
      const { canvasObjectId, assetKey, object, fileName, thumbnail } = event.detail || {};

      if (!canvasObjectId) {
        return;
      }

      if (!Array.isArray(this.state.logos)) {
        this.$set(this.state, 'logos', []);
      }

      const exists = this.state.logos.some((logo) => {
        return String(logo.canvas_object_id) === String(canvasObjectId);
      });

      if (!exists) {
        this.state.logos.push({
          canvas_object_id: canvasObjectId,
          assetKey: assetKey || object?.displayBuilderAssetKey || null,
          fileName: fileName || object?.fileName || '',
          thumbnail: thumbnail || object?._element?.currentSrc || object?._element?.src || null,
        });
      }

      this.markDisplayBuilderDirty();
    },
    onCanvasLogoDeleted(event) {
      const canvasObjectId = event.detail?.canvasObjectId;

      if (!canvasObjectId || !Array.isArray(this.state.logos)) {
        return;
      }

      const before = this.state.logos.length;

      this.state.logos = this.state.logos.filter((logo) => {
        return String(logo.canvas_object_id) !== String(canvasObjectId);
      });

      if (this.state.logos.length === before) {
        return;
      }

      this.markDisplayBuilderDirty();
      this.markDisplayBuilderDirty.flush?.();
    },
    removeLogo(logo) {
      if (!logo?.canvas_object_id) {
        return;
      }

      const canvas = this.getDisplayBuilderCanvas();
      if (canvas) {
        const target = canvas.getObjects().find((object) => {
          return String(object.id) === String(logo.canvas_object_id);
        });

        if (target) {
          this.setActiveFabricObject(canvas, target);
          editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
            action: 'deleteConfirmation', value: null
          })
        }
      }

      if (Array.isArray(this.state.logos)) {
        this.state.logos = this.state.logos.filter((item) => {
          return String(item.canvas_object_id) !== String(logo.canvas_object_id);
        });
      }

      this.markDisplayBuilderDirty();
    },
    getLogoCanvasObjectIds() {
      return new Set(
        (this.state.logos || [])
          .map((logo) => String(logo.canvas_object_id || ''))
          .filter(Boolean)
      );
    },
    setBackgroundObjectsLockState(isLocked = true) {
      const canvas = this.getDisplayBuilderCanvas();

      if (!canvas) {
        return;
      }

      const logoIds = this.getLogoCanvasObjectIds();

      const bgs = canvas.getObjects().filter((obj) => {
        const id = String(obj.id || '');

        return (
          id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID)
          || id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID)
          || obj.fieldType === FIELD_TYPE_BACKGROND_THEME_IMAGE
        )
          && !logoIds.has(id);
      });

      bgs.forEach((object) => {
        object.set({
          locked: isLocked,
          selectable: !isLocked,
          evented: !isLocked,
          lockMovementX: isLocked,
          lockMovementY: isLocked,
          lockScalingX: isLocked,
          lockScalingY: isLocked,
          lockRotation: isLocked,
          hasControls: !isLocked,
          hasBorders: !isLocked,
          hoverCursor: isLocked ? 'default' : 'move',
          moveCursor: 'move',
        });

        object.setCoords();
        object.canvas.fire('locked:changed', object);
      });

      if (isLocked) {
        const activeObjects = canvas.getActiveObjects?.() || [];
        const hasActiveBackground = activeObjects.some((object) => bgs.includes(object));

        if (hasActiveBackground) {
          canvas.discardActiveObject();
        }
      }

      canvas.requestRenderAll();
    },
    openLightboxModal(product) {
      this.$refs.boxPreview.show(product?.url || product?.thumbnail);
    },
    onShelfLoadCompleted() {
      this.markDisplayBuilderDirty({ onShelfLoaded: true });
      this.markDisplayBuilderDirty.flush?.();
    }
  },
};
</script>

<style scoped>
:root {
  --teal:       #2eb89a;
  --teal-light: #e8f7f3;
  --teal-dim:   rgba(46,184,154,.12);
  --border:     #e8eaed;
  --border-mid: #d5d9df;
  --bg:         #ffffff;
  --bg-soft:    #f8f9fb;
  --bg-hover:   #f2f4f7;
  --text:       #1a1d23;
  --text-muted: #6b7280;
  --text-light: #9ca3af;
  --danger:     #ef4444;
  --danger-bg:  #fef2f2;
  --radius:     10px;
  --radius-sm:  6px;
  --shadow:     0 1px 3px rgba(0,0,0,.07);
}

.sp-panel {
  background: var(--bg);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-bottom: 24px;
  width: 100%;
  min-width: 0;
  max-width: 580px;
}

/* ── Logo bar ── */
.sp-logo-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.75rem;
  border: 1px solid var(--border);
  border-radius: 6px;
}

.sp-logo-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 500;
  color: var(--teal);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity .15s;
}
.sp-logo-btn:hover { opacity: .75; }

/* ── Title row ── */
.sp-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
}

.sp-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -.2px;
}

.sp-title__icon { color: var(--teal); flex-shrink: 0; }

/* ── Icon button ── */
.sp-icon-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-light);
  cursor: pointer;
  transition: background .13s, color .13s, border-color .13s;
}
.sp-icon-btn:hover,
.sp-icon-btn.active {
  background: var(--teal-dim);
  border-color: var(--teal);
  color: var(--teal);
}

/* ── Search ── */
.sp-search-wrap {
  padding: 0 16px 10px;
}

.sp-search {
  width: 100%;
  font-size: 13px;
  color: var(--text);
  background: var(--bg-soft);
  border: 1.5px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 7px 11px;
  outline: none;
  transition: border-color .14s, box-shadow .14s;
}
.sp-search:focus {
  border-color: var(--teal);
  box-shadow: 0 0 0 3px var(--teal-dim);
}
.sp-search::placeholder { color: var(--text-light); }

/* ── Tabs ── */
.sp-tabs {
  display: flex;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  background: var(--bg-soft);
  overflow-x: auto;
}

.sp-tab {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0.375rem 0.75rem;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-muted);
  background: transparent;
  border: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color .14s, background .14s, border-color .14s;
}
.sp-tab:hover { color: var(--text); background: var(--bg-hover); }
.sp-tab.active {
  color: var(--teal);
  background: var(--bg);
  border-bottom-color: var(--teal);
  font-weight: 600;
}

.sp-tab__badge {
  font-size: 10.5px;
  font-weight: 500;
  color: var(--text-light);
}
.sp-tab.active .sp-tab__badge { color: var(--teal); }

/* ── Shelves container ── */
.sp-shelves {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── Single shelf ── */
.sp-shelf {
  margin-top: 10px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  cursor: pointer;
  overflow: hidden;
  transition: border-color .12s, box-shadow .12s;
}

.sp-shelf--active {
  border-color: var(--teal);
  box-shadow: 0 0 0 2px var(--teal-dim), var(--shadow);
}

.sp-shelf--active .sp-shelf__head {
  background: var(--teal-light);
}

.sp-shelf__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px 7px;
  border-bottom: 1px solid var(--border);
  background: var(--bg-soft);
}

.sp-shelf__meta {
  display: flex;
  align-items: baseline;
  gap: 7px;
}

.sp-shelf__name {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--text);
}

.sp-shelf__count {
  font-size: 11px;
  color: var(--text-light);
}

.sp-shelf__actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

/* ── Action button (small icon) ── */
::v-deep .sp-action-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-light);
  cursor: pointer;
  transition: background .12s, color .12s;
}
::v-deep.sp-action-btn:hover { background: var(--bg-hover); color: var(--text-muted); }
.sp-action-btn--danger:hover { background: var(--danger-bg); color: var(--danger); }

/* ── Products ── */
.sp-products {
  display: flex;
  flex-direction: column;
}

.sp-product {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border);
  transition: background .12s;
}
.sp-product:last-child { border-bottom: none; }
.sp-product:hover { background: var(--bg-soft); }
.sp-product--highlight { background: var(--teal-light) !important; }

/* Product image */
.sp-product__img {
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
}
.sp-product__img img { width: 100%; height: 100%; object-fit: contain; }
.sp-product__img-placeholder { color: var(--text-light); display: flex; }

/* Product info */
.sp-product__info {
  flex: 1;
  min-width: 0;
}
.sp-product__name {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text);
  margin: 0 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sp-product__sku {
  font-size: 11px;
  color: var(--text-light);
  margin: 0;
  font-variant-numeric: tabular-nums;
}

/* ── Stepper ── */
.sp-stepper {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1.5px solid var(--border-mid);
  border-radius: var(--radius-sm);
  overflow: hidden;
  flex-shrink: 0;
}

.sp-stepper__btn {
  width: 26px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-soft);
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  transition: background .12s, color .12s;
}
.sp-stepper__btn:hover:not(:disabled) { background: var(--teal-dim); color: var(--teal); }
.sp-stepper__btn:disabled { opacity: .35; }

.sp-stepper__val {
  min-width: 24px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  border-left: 1px solid var(--border);
  border-right: 1px solid var(--border);
  padding: 0 2px;
  line-height: 28px;
  font-variant-numeric: tabular-nums;
}

.sp-add-product {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-muted);
  background: var(--bg-soft);
  border: none;
  border-top: 1px dashed var(--border-mid);
  cursor: pointer;
  transition: background .12s, color .12s;
}
.sp-add-product:hover { background: var(--teal-dim); color: var(--teal); }

.sp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 36px 16px;
  text-align: center;
  color: var(--text-light);
}
.sp-empty p { font-size: 13px; margin: 0; line-height: 1.5; }
.sp-empty strong { color: var(--text-muted); }

.sp-slide-enter-active, .sp-slide-leave-active {
  transition: max-height .2s ease, opacity .2s ease;
  overflow: hidden;
  max-height: 60px;
}
.sp-slide-enter, .sp-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.sp-list-enter-active, .sp-list-leave-active { transition: opacity .18s, transform .18s; }
.sp-list-enter, .sp-list-leave-to { opacity: 0; transform: translateY(-6px); }
.sp-list-move { transition: transform .2s; }

.shelves-container {
  height: 560px;
  overflow: auto;
}

::v-deep .sp-product,
::v-deep .sp-products,
::v-deep .sp-shelf,
::v-deep .sp-shelves {
  overflow: visible;
}
.btn-move-fixture {
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 13px;
  border-radius: 5px;
  transition: color 0.15s, background 0.15s;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s, background 0.15s;
}
.btn-move-fixture:hover {
  color: #3c4b64;
}

.sp-shelf__actions:hover .btn-move-fixture:not(:disabled) {
  opacity: 1;
}
.sp-shelf__actions:hover .btn-move-fixture:disabled {
  opacity: 0.25;
  cursor: default;
  pointer-events: none;
}

.btn-move-fixture:disabled:hover {
  color: var(--text-muted);
  background: none;
}

.sp-tabs {
    overflow-x: auto;
    scrollbar-width: none;
}

/* Chrome / Edge / Safari */
.sp-tabs::-webkit-scrollbar {
    height: 0;
}

 /* Firefox */
.sp-tabs:hover {
    scrollbar-width: thin;
}

.sp-tabs:hover::-webkit-scrollbar {
    height: 4px;
}

.sp-tabs:hover::-webkit-scrollbar-track {
    background: transparent;
}

.sp-tabs:hover::-webkit-scrollbar-thumb {
    background: #d0d5dd;
    border-radius: 999px;
}

.sp-tabs:hover::-webkit-scrollbar-thumb:hover {
    background: #98a2b3;
}
@media (max-width: 1800px) {
    .sp-panel {
      width: 93%;
    }
}
.zoom-container {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffffffb3;
  transition-property: opacity;
  transition-timing-function: cubic-bezier(.4, 0, .2, 1);
  transition-duration: .15s;
  opacity: 0;
}
.sp-product__img:hover .zoom-container {
  opacity: 1;
}
</style>
